/**
 * ITALIANO - la traduzione
 * -------------------------
 * A translation OF `en.js`, which stays the reference. A key missing here
 * falls back to the English rather than to a blank, so the site is never
 * broken by a gap - it is only less translated. Run
 * `npm run check-translations --prefix frontend` to see the gaps, because a
 * fallback is invisible by design and that is exactly how a translation stays
 * half finished for a year.
 *
 * FOUR DECISIONS TAKEN ONCE, HERE, SO THE WHOLE SITE READS AS ONE VOICE
 *
 * 1. **"lavoretto", not "faccenda" or "compito".** A `chore` here is a small
 *    paid job - an afternoon's gardening, an evening babysitting. "Faccenda"
 *    is a household task you do yourself; "compito" is homework. "Lavoretto"
 *    is exactly the casual paid work this site is about, and it is the word a
 *    family in Varese would actually use.
 *
 * 2. **"tu", never "Lei".** The site speaks plainly in English and it should
 *    speak plainly in Italian. "Lei" would be more formal than the English and
 *    would sit oddly with sixteen-year-olds, who are half the users.
 *
 * 3. **Phrases are written so they do not need a gender.** Italian makes
 *    adjectives agree, so a literal "Welcome back" is "Bentornato" (male) or
 *    "Bentornata" (female), and "I'm a student" is "studente" or
 *    "studentessa". Rather than "Bentornato/a" everywhere - which is ugly and
 *    still reads as an afterthought - the phrasing sidesteps it: the login
 *    title is "Accedi", and the signup buttons are "Cerco un lavoretto" and
 *    "Ho un lavoretto da far fare", which happen to be better calls to action
 *    than the English anyway. **Keep this up when adding phrases.** Half the
 *    students on this site are girls and the site should not guess.
 *
 * 4. **Categories are TRANSLATED FOR DISPLAY ONLY.** The keys are the exact
 *    values stored in the database (a Mongoose enum). Never change a key.
 *    See the warning at the top of en.js.
 */

export default {
  common: {
    loading: 'Caricamento...',
    save: 'Salva',
    saving: 'Salvataggio...',
    cancel: 'Annulla',
    back: 'Indietro',
    next: 'Avanti',
    yes: 'Sì',
    no: 'No',
    close: 'Chiudi',
    edit: 'Modifica',
    delete: 'Elimina',
    confirm: 'Conferma',
    somethingWrong: 'Qualcosa è andato storto. Riprova.',
    perHour: 'circa {amount} all’ora',
    loadingConversations: 'Caricamento delle conversazioni...',
    checkingLogin: 'Verifica dell’accesso...',
    loadingChore: 'Caricamento del lavoretto...',
    checkingLink: 'Verifica del link...',
    findingChores: 'Ricerca dei lavoretti...',
    loadingMessages: 'Caricamento dei messaggi...',
    loadingNotifications: 'Caricamento delle notifiche...',
    backToMyChores: 'Torna ai miei lavoretti',
    welcomeBack: 'Bentornato, {name}.',
  },

  language: {
    label: 'Lingua',
    change: 'Cambia lingua',
    it: 'Italiano',
    en: 'English',
  },

  nav: {
    browse: 'Cerca lavoretti',
    myOffer: 'La mia disponibilità',
    myWork: 'I miei lavori',
    myChores: 'I miei annunci',
    whosFree: 'Studenti disponibili',
    students: 'Studenti',
    moderation: 'Moderazione',
    messages: 'Messaggi',
    notifications: 'Notifiche',
    logIn: 'Accedi',
    signUp: 'Registrati',
    logOut: 'Esci',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    messagesUnread: 'Messaggi, {count} da leggere',
    notificationsUnread: 'Notifiche, {count} da leggere',
    users: 'Committenti',
  },

  home: {
    headlineA: 'Quei lavoretti che rimandi sempre.',
    headlineB: 'Studenti che preferirebbero guadagnare.',
    lede: 'Pubblica cosa c’è da fare e quanto paghi. Gli studenti della scuola si candidano, tu scegli in base alle recensioni.',
    // Deliberately not "Sono uno studente" - see decision 3 at the top. These
    // two also mirror each other better than the English does.
    ctaPoster: 'Ho un lavoretto da far fare',
    ctaStudent: 'Cerco un lavoretto',
    ctaBrowse: 'Guarda la bacheca',
    costFree: 'È gratis, per tutti e due.',
    costPaid: 'Cinque euro all’anno per pubblicare.',
    costBody: 'Gli studenti tengono tutto quello che guadagnano, basta concordare insieme un prezzo e organizzare il pagamento tra voi.',
    boardTitle: 'In bacheca adesso',
    boardBody: 'Lavoretti pubblicati da persone legate alla scuola.',
    seeAll: 'Vedi tutti ({count})',
    closeTitle: 'Dai un’occhiata prima di registrarti.',
    closeBrowse: 'Guarda cosa c’è',
    closeSignUp: 'Crea un account',
  },

  login: {
    // "Bentornato" would have to guess a gender - see decision 3.
    title: 'Accedi',
    subtitle: 'Entra per vedere i tuoi lavoretti, le candidature e i messaggi.',
    email: 'Indirizzo email',
    password: 'Password',
    forgot: 'Password dimenticata?',
    submit: 'Accedi',
    noAccount: 'Non hai un account?',
    signUp: 'Registrati',
    checkEmail: 'Controlla la tua email',
    yourCode: 'Il tuo codice',
  },

  signupChoice: {
    title: 'Che tipo di account?',
    posterTitle: 'Ho dei lavoretti da far fare',
    posterBody: 'Pubblica annunci e scegli lo studente che li farà.',
    studentTitle: 'Voglio guadagnare',
    studentBody: 'Candidati ai lavoretti vicino a te e indica quando sei disponibile.',
    studentBodyNoOffers: 'Trova lavoretti vicino a te e candidati a quelli che fanno per te.',
    haveAccount: 'Hai già un account?',
    subtitle: 'Due tipi di account, a seconda del lato del lavoretto da cui ti trovi.',
    studentCta: 'Registrati come studente',
    posterCta: 'Registrati per pubblicare lavoretti',
  },

  marketplace: {
    title: 'Lavoretti',
    subtitle: 'Annunci pubblicati da persone vicino alla scuola.',
    search: 'Cerca',
    searchPlaceholder: 'giardino, cane, spesa...',
    category: 'Categoria',
    allCategories: 'Tutte le categorie',
    minPay: 'Paga minima',
    maxPay: 'Paga massima',
    withinKm: 'Entro (km)',
    sort: 'Ordina per',
    sortNewest: 'Più recenti',
    sortSoonest: 'In programma prima',
    sortPay: 'Pagati meglio',
    sortDistance: 'Più vicini',
    sortRelevance: 'Più pertinenti',
    moreFilters: 'Altri filtri',
    fewerFilters: 'Meno filtri',
    filtersSet: '{count} attivi',
    empty: 'Non c’è ancora niente in bacheca.',
    emptyFiltered: 'Nessun lavoretto corrisponde a quello che cerchi.',
    resultCount: { one: '{count} lavoretto', other: '{count} lavoretti' },
  },

  chore: {
    noBackup: 'Senza riserva',
    applyAsBackup: 'Candidati come riserva',
    aboutHours: 'circa {hours}h',
    aboutHoursEach: 'circa {hours}h ogni volta',
    visits: { one: '{count} volta', other: '{count} volte' },
    daysFrom: '{count} giorni dal {date}',
    payEach: '{amount} × {count}',
    eachTime: '{amount} ogni volta',
    totalInAll: '{amount} in tutto',
  },

  status: {
    open: 'Aperto alle candidature',
    assigned: 'Assegnato',
    completed: 'Completato',
    cancelled: 'Annullato',
    expired: 'Scaduto: nessuna candidatura',
  },

  /* I KEYS SONO I VALORI SALVATI NEL DATABASE - non cambiarli mai.
     Solo il testo a destra viene mostrato. */
  categories: {
    Cleaning: 'Pulizie',
    Gardening: 'Giardinaggio',
    Babysitting: 'Babysitting',
    'Pet Care': 'Cura degli animali',
    Tutoring: 'Ripetizioni',
    'Shopping & Errands': 'Spesa e commissioni',
    'Moving & Lifting': 'Traslochi e trasporti',
    'Tech Help': 'Aiuto con la tecnologia',
    'DIY & Repairs': 'Fai da te e riparazioni',
    Other: 'Altro',
  },



  forgot: {
    title: 'Password dimenticata?',
    subtitle: 'Dicci il tuo indirizzo email e ti manderemo un link per impostarne una nuova.',
    email: 'Indirizzo email',
    submit: 'Mandami il link',
    sending: 'Invio in corso...',
    checkSpam: 'Il link è valido per un’ora. Se non arriva niente, controlla la posta indesiderata e verifica di aver scritto lo stesso indirizzo con cui hai creato l’account.',
    backToLogin: 'Torna ad accedere',
    backToLogIn: 'Torna all’accesso',
  },

  confirmEmail: {
    failedTitle: 'Non siamo riusciti a confermare quel link',
    alreadyDone: 'Se hai già confermato non devi fare altro: accedi e basta.',
    goToLogin: 'Vai al login',
    thanksNamed: 'Grazie, {name}',
    thanks: 'Grazie',
    done: 'Il tuo indirizzo email è confermato. Ora puoi pubblicare lavoretti, candidarti e organizzarti con le persone.',
    goToDashboard: 'Vai alla mia bacheca',
  },

  settings: {
    languageTitle: 'Lingua',
    languageBody: 'Cambia subito la lingua del sito. È anche la lingua in cui ti scriviamo: le email seguono questa impostazione.',
    languageSaved: 'Salvato. Da adesso le email arriveranno in {language}.',
  },


  twoFactor: {
    sentTo: 'Abbiamo inviato un codice di sei cifre a {email}. È valido per i prossimi {minutes} minuti.',
    noCode: 'Non ti arriva il codice? Controlla la posta indesiderata, oppure usa uno dei tuoi codici di recupero.',
    checking: 'Verifica in corso...',
    startAgain: 'Ricomincia',
    tooSlow: 'Hai impiegato troppo tempo. Inserisci di nuovo la password.',
    sendNewCode: 'Invia un nuovo codice',
  },

  reset: {
    titleDone: 'Password cambiata',
    title: 'Scegli una nuova password',
    done: 'La tua password è stata cambiata. Ti portiamo alla pagina di accesso...',
    newPassword: 'Nuova password',
    rule: 'Almeno 8 caratteri, di cui almeno un numero.',
    again: 'Riscrivila',
    submit: 'Imposta la nuova password',
    mismatch: 'Le due password non coincidono.',
    tooWeak: 'La password deve avere almeno 8 caratteri e contenere un numero.',
    logInNow: 'Accedi ora',
    requestNewLink: 'Richiedi un nuovo link',
  },


  form: {
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Indirizzo email',
    password: 'Password',
    phone: 'Numero di telefono',
    optional: 'Facoltativo',
    passwordRule: 'Almeno 8 caratteri, di cui almeno un numero.',
    showPassword: 'Mostra la password',
    hidePassword: 'Nascondi la password',
  },

  terms: {
    agree: 'Ho letto e accetto i',
    termsLink: 'termini di servizio',
    and: 'e l’',
    privacyLink: 'informativa sulla privacy',
    keptWhileReading: 'Quello che hai scritto viene conservato mentre leggi: la freccia indietro ti riporta esattamente dov’eri.',
  },

  school: {
    label: 'La tua scuola',
    placeholder: 'La scuola che frequenti',
    joiningCode: 'Codice di invito',
    codePlaceholder: 'Il codice che ti hanno dato',
    joiningCodeHint: 'Ce l’ha chi ti ha invitato su ChoreConnect. È quello che mantiene il sito riservato a chi è stato invitato davvero. Maiuscole e spazi non fanno differenza.',
  },

  location: {
    address: 'Indirizzo',
    yourAddress: 'Il tuo indirizzo',
    town: 'Comune',
    townHint: 'Inizia a scrivere e scegli dall’elenco: è quello che fa funzionare le distanze.',
    addressHint: 'Visibile solo allo studente che accetti e alla riserva: mai nell’annuncio pubblico.',
    usePrecise: 'Usa la mia posizione esatta',
    updatePrecise: 'Aggiorna a dove sono adesso',
    findingYou: 'Ti stiamo localizzando...',
    noBrowser: 'Il tuo browser non può condividere la posizione.',
    refused: 'Permesso negato. Scrivi comune e via a mano.',
    failed: 'Non siamo riusciti a capire dove sei. Scrivi comune e via a mano.',
    nothingHere: 'Non siamo riusciti a localizzarti. Scrivi comune e via a mano.',
    noStreet: 'Non abbiamo trovato la via. Scrivila tu.',
    tooVague: 'Il telefono sa dove sei solo entro circa {km} km. Scrivi comune e via a mano.',
    addressNotFound: 'Non abbiamo trovato quella via. Controlla come è scritta e il comune.',
    findFailed: 'Non è stato possibile cercarla adesso.',
    tooMany: 'Troppe ricerche per ora. Aspetta qualche minuto.',
    creditBefore: 'Nomi delle vie:',
    creditLink: '© contributori di OpenStreetMap',
  },

  userSignup: {
    title: 'Pubblica lavoretti su ChoreConnect',
    subtitlePaid: 'Crea il tuo account, poi iscriviti a 5 € all’anno per iniziare a pubblicare.',
    subtitleFree: 'Crea il tuo account e pubblica il primo lavoretto. È gratis.',
    creating: 'Creazione dell’account...',
    create: 'Crea account',
    areYouStudent: 'Sei uno studente?',
    signUpHere: 'Registrati qui invece',
    registered: 'Hai già un account?',
  },


  applicationStatus: {
    pending: 'In attesa di risposta',
    accepted: 'Accettata',
    backup: 'Riserva',
    rejected: 'Non scelta',
    cancelled: 'Lavoretto annullato',
    withdrawn: 'Ritirata',
  },

  review: {
    howWasIt: 'Com’è andata con {name}?',
    yourReview: 'La tua recensione (facoltativa)',
    placeholder: 'Com’è andata? Lavoreresti di nuovo con questa persona?',
    submit: 'Invia la recensione',
    chooseStars: 'Scegli quante stelle dare.',
    noneYet: 'Ancora nessuna recensione',
  },

  birthDate: {
    day: 'Giorno',
    dayShort: 'GG',
    month: 'Mese',
    year: 'Anno',
    impossible: 'Questa data non esiste: controlla il giorno e il mese.',
  },

  studentSignup: {
    title: 'Inizia a guadagnare con ChoreConnect',
    subtitle: 'Iscriversi è gratis. Guarda i lavoretti vicino a te e candidati a quelli che ti interessano.',
    age: 'Età',
    ageRule: 'Devi avere almeno 16 anni per lavorare tramite ChoreConnect.',
    dateOfBirth: 'Data di nascita',
    dateOfBirthHint: 'Devi avere almeno 16 anni. Gli altri vedono solo la tua età, mai questa data.',
    dateOfBirthAge: 'Quindi hai {age} anni. Gli altri vedono solo la tua età, mai questa data.',
    schoolYear: 'Anno scolastico',
    about: 'Parlaci di te',
    aboutPlaceholder: 'Due righe su cosa sai fare e quando sei disponibile. È la prima cosa che si legge quando si sceglie chi prendere.',
    skills: 'Cosa te la senti di fare?',
    skillsHint: 'Scegline quanti vuoi: compaiono sul tuo profilo.',
    guardianTitle: 'Dato che hai meno di 18 anni, serve il consenso di un genitore o di chi ne fa le veci.',
    guardianBody: 'Scriviamo direttamente a loro: devono confermare loro, non tu. Verrà chiesto il consenso per cinque cose: candidarsi ai lavoretti, i lavoretti che si ripetono per più giorni, pubblicare quando sei disponibile, scrivere alla persona di cui accetti il lavoretto, e gli avvisi sul telefono. Puoi guardarti intorno subito, ma non puoi candidarti finché non rispondono.',
    guardianName: 'Nome del genitore o tutore',
    guardianEmail: 'Il loro indirizzo email',
    guardianEmailHint: 'Deve essere il loro, non il tuo.',
    needChores: 'Hai dei lavoretti da far fare?',
    signUpHere: 'Registrati qui',
  },

  marketplaceExtra: {
    heading: 'Lavoretti vicino a te',
    subtitleStudent: 'Trova un lavoretto che si incastri con la scuola. Candidati a quelli che ti interessano e chi ha pubblicato ti risponderà.',
    subtitleOther: 'Tutto quello che al momento accetta candidature nella zona.',
    searching: 'Ricerca in corso...',
    found: { one: '{count} lavoretto trovato', other: '{count} lavoretti trovati' },
    emptyTitle: 'Nessun lavoretto corrisponde',
    emptyFiltered: 'Prova ad allargare i filtri: più distanza, oppure una paga minima più bassa.',
    emptyNone: 'Al momento non c’è niente di aperto. Riprova tra un po’.',
    looseSearch: 'Nessun lavoretto contiene tutte le parole che hai scritto: ecco quelli che ci si avvicinano di più.',
  },


  consent: {
    linkInvalid: 'Questo link non è valido.',
    titleNew: 'Una richiesta di consenso',
    titleRenewal: 'Le chiediamo di dare un’altra occhiata',
    introNew: '{fullName} ha creato un account su ChoreConnect. Avendo meno di 18 anni, non può succedere nulla finché Lei non dà il consenso.',
    introRenewal: 'ChoreConnect fa più cose di quando ha dato il consenso per {name}, quindi preferiamo richiederglielo piuttosto che darlo per scontato.',
    changedTitle: 'Cosa è cambiato dall’ultima volta.',
    changedBody: '{name} ora può ricevere richieste per lavoretti che durano più giorni, pubblicare quando è disponibile perché gli adulti lo vedano, scrivere alla persona di cui ha accettato il lavoretto e ricevere avvisi sul telefono. Trova tutto qui sotto.',
    agreedOn: 'Ha dato il consenso alla versione precedente il {date}.',
    untilYouAnswer: 'Finché non risponde, {name} non può candidarsi né ricevere richieste da nessuno.',
    already: 'Ha già confermato. Non deve fare altro.',
    whatIsIt: 'Cos’è ChoreConnect',
    whatIsItBody: 'Una bacheca locale dove si pubblicano piccoli lavori - giardinaggio, pulizie, babysitting, ripetizioni, aiuto con la spesa - e gli studenti della scuola si candidano per farli a un compenso concordato.',
    introOnlyTitle: 'Siamo soltanto un servizio di presentazione.',
    introOnlyBody: 'Non assumiamo nessuno, non gestiamo il denaro e non supervisioniamo il lavoro. Il pagamento viene concordato direttamente tra Suo figlio o Sua figlia e la persona che ha pubblicato il lavoretto.',
    whatYouAgree: 'Cosa sta autorizzando',
    whatWeHold: 'E cosa conserviamo',
    fullDetail: 'Tutti i dettagli sono nella nostra',
    privacyNotice: 'informativa sulla privacy',
    andThe: 'e nei',
    termsOfService: 'termini di servizio',
    readFirst: '. La preghiamo di leggerli prima di decidere.',
    schoolYear: 'Anno scolastico',
    school: 'Scuola',
    yourDecision: 'La Sua decisione',
    agreeNew: 'Sì, autorizzo {name} a usare ChoreConnect',
    agreeRenewal: 'Sì, confermo il consenso per {name}',
    declineNew: 'No, non autorizzo',
    declineRenewal: 'No, preferisco che smetta',

    agree1Title: 'Candidarsi ai lavoretti',
    agree2Title: 'Lavoretti che si ripetono per più giorni',
    agree3Title: 'Indicare quando è disponibile',
    agree4Title: 'Messaggi su un lavoretto concordato',
    agree5Title: 'Avvisi sul telefono',

    agree1Body: '{name} può candidarsi ai lavoretti pubblicati sul sito e recarsi all’indirizzo di chi lo accetta. Sceglie a cosa candidarsi: nessun lavoro viene assegnato d’ufficio.',
    agree2Body: 'Un lavoretto può durare più di un giorno: portare a spasso un cane ogni mattina per una settimana, o fare da babysitter ogni mercoledì e sabato per un massimo di tre mesi. Quindi {name} può accettare una volta sola di andare più volte a casa della stessa persona, ed è bene saperlo in modo esplicito. Vede tutte le date prima di accettare, niente si ripete all’infinito, e può rinunciare a una singola giornata fino a 24 ore prima - oppure interrompere l’intero accordo - senza alcuna penalizzazione.',
    agree3Body: '{name} può pubblicare gli orari in cui è disponibile, e gli adulti iscritti al sito possono vederli e chiedere di un orario preciso. Non gli altri studenti, e nessuno che non abbia effettuato l’accesso. Niente viene fissato se {name} non dice di sì, e l’indirizzo viene condiviso solo dopo che ha accettato.',
    agree4Body: 'Una volta concordato un lavoretto, {name} e quella singola persona possono scriversi qui per organizzarsi: quale porta, cosa portare, se si fa tardi. Solo quella persona, solo quel lavoretto, e la conversazione si chiude una settimana dopo. I messaggi sono privati, ma vengono conservati, e un moderatore può leggere una conversazione in due casi: se qualcuno la segnala, oppure se il sito rileva uno scambio di contatti - un numero di telefono, un indirizzo email, la richiesta di continuare altrove. Il secondo caso esiste perché un ragazzo o una ragazza convinti a spostarsi fuori dal sito di solito non lo segnalano. Al di fuori di questi casi nessuno legge le conversazioni, e ogni volta che un moderatore ne apre una viene registrato.',
    agree5Body: 'Se {name} li attiva, il sito può mandare avvisi sul telefono quando compaiono nuovi lavoretti. Sono disattivati finché non li sceglie, si possono disattivare in qualsiasi momento, e non arriva nulla tra le 21:00 e le 7:30.',

    hold0: 'Nome, data di nascita, scuola, anno scolastico, comune di {name} e tutto quello che scrive su di sé. Gli altri utenti vedono la sua età, mai la data.',
    hold1: 'Le recensioni che lascia e che riceve, mostrate pubblicamente con il nome e l’iniziale del cognome.',
    hold2: 'I messaggi descritti sopra, conservati come registro anziché cancellati.',
    hold3: 'Il Suo nome e indirizzo email, unicamente come registro di questo consenso.',
    askChildResend: 'Chiedi a tuo figlio o a tua figlia di accedere e di scegliere “invia di nuovo” dalla propria bacheca: arriverà un nuovo link.',
    thankYouConfirmed: 'Grazie. Ora {name} può usare ChoreConnect.',
    thankYouDeclined: 'Grazie. Abbiamo registrato il tuo diniego: {name} non può candidarsi ai lavoretti né essere prenotato da nessuno.',
    copyEmailed: 'Ti abbiamo inviato una copia per i tuoi archivi, con l’elenco di ciò che hai autorizzato. Puoi ritirare il consenso quando vuoi rispondendo a quella email, e chiuderemo l’account. Se cambieremo il funzionamento del sito, te lo chiederemo di nuovo invece di darlo per scontato.',
    findOutMore: 'Scopri di più su ChoreConnect',
    whatTheyToldUs: 'Che cosa ci ha detto {name}',
    name: 'Nome',
    age: 'Età',
    ifWrong: 'Se qualcosa non è corretto, o se non ti aspettavi questa email, rifiuta qui sotto.',
  },


  notifications: {
    title: 'Notifiche',
    unread: '{count} da leggere',
    caughtUp: 'Non c’è nient’altro da leggere.',
    markAll: 'Segna tutte come lette',
    emptyTitle: 'Ancora niente',
    emptyBody: 'Quando qualcuno si candida a un tuo lavoretto, ti accetta per un lavoro o lascia una recensione, lo trovi qui.',
    remove: 'Rimuovi',
  },

  messages: {
    seeArchived: 'Archiviate',
    backToInbox: 'Torna alle conversazioni',
    putBack: 'Ripristina',
    aQuestion: 'una domanda',
    aboutTheirTimes: 'Sulla sua disponibilità',
    title: 'Messaggi',
    subtitle: 'Una conversazione per ogni lavoretto. Uno studente può fare una domanda su un lavoretto aperto alle candidature, e le due persone di un lavoretto concordato possono parlarne qui.',
    emptyTitle: 'Ancora nessuna conversazione',
    emptyBody: 'Una volta concordato un lavoretto, tu e l’altra persona potete scrivervi qui per mettervi d’accordo sui dettagli.',
    you: 'Tu:',
  },

  directory: {
    title: 'Studenti',
    subtitle: 'Tutti quelli disponibili, con i più apprezzati per primi. Apri il profilo per leggere le recensioni prima di decidere.',
    schoolYear: 'Anno scolastico',
    anyYear: 'Qualsiasi anno',
    minRating: 'Valutazione minima',
    anyRating: 'Qualsiasi valutazione',
    searching: 'Ricerca in corso...',
    count: { one: '{count} studente', other: '{count} studenti' },
    emptyTitle: 'Nessuno studente corrisponde',
    emptyBody: 'Prova a togliere i filtri o a cercare qualcosa di più generico.',
    searchPlaceholder: 'Nome, o qualcosa che sa fare',
    fourUp: 'Da 4 stelle in su',
    fourHalfUp: 'Da 4,5 stelle in su',
    fiveOnly: 'Solo 5 stelle',
    usersTitle: 'Chi pubblica lavoretti',
    usersSubtitle: 'Quello che gli adulti del sito mostrano agli studenti. Una foto, un nome e un comune sono tutto ciò che si vede prima di decidere se andare a casa di quella persona.',
    searchPeople: 'Cerca',
    usersSearchPlaceholder: 'Nome o indirizzo email',
    includeSuspended: 'Includi le persone sospese',
    loadingUsers: 'Caricamento delle persone...',
    noUsers: 'Nessuno da mostrare',
    noUsersBody: 'Nessun account corrisponde. Prova una ricerca più breve, oppure includi le persone sospese.',
    usersFound: { one: '{count} persona', other: '{count} persone' },
    backToModeration: 'Torna alla moderazione',
    studentsIncludeSuspended: 'Includi gli studenti sospesi',
  },

  notFound: {
    title: 'Pagina non trovata',
    body: 'Questa pagina non esiste. Potrebbe essere stata spostata, oppure il lavoretto potrebbe essere stato rimosso.',
    home: 'Torna alla pagina iniziale',
  },


  dashboard: {
    cancelStillFine: 'Puoi ancora annullare: inizia {when}.',
    cancelLateNote: 'Inizia {when}. Puoi ancora annullare, ma dovrai dire perché.',
    hello: 'Ciao, {name}',
    posterSubtitle: 'Tutto quello che hai pubblicato, e quello che aspetta una tua risposta.',
    studentSubtitle: 'I tuoi lavori e le tue candidature finora.',
    loadingPoster: 'Carico la tua bacheca...',
    loadingStudent: 'Carico i tuoi lavori...',
    postChore: 'Pubblica un lavoretto',
    membershipInactive: 'Il tuo abbonamento non è attivo.',
    membershipBody: 'Puoi guardare e gestire quello che hai, ma non puoi pubblicare niente di nuovo.',
    setUpMembership: 'Attiva l’abbonamento (5 €/anno)',
    applicationsWaiting: 'Candidature in attesa',
    choresCompleted: 'Lavoretti completati',
    waitingToHear: 'In attesa di risposta',
    yourRating: 'La tua valutazione',
    comingUp: 'In programma',
    whoApplied: 'Chi si è candidato',
    waitingOnYou: { one: '{count} studente aspetta una risposta', other: '{count} studenti aspettano una risposta' },
    seeWhoApplied: 'Vedi chi si è candidato',
    yourChores: 'I tuoi lavoretti',
    reportSent: 'Inviato. Un moderatore legge tutte le segnalazioni alle 20:00.',
    workYouHaveDone: 'Lavoretti che hai fatto',
    yourApplications: 'Le tue candidature',
    reviewsToLeave: 'Recensioni da lasciare',
    youAreBackup: 'Sei la riserva',
    yours: 'Tuo',
    awaitingDecision: 'In attesa di una decisione.',
    nothingYet: 'Ancora niente qui',
    applicationWithdrawn: 'Candidatura ritirata.',
    findMoreWork: 'Cerca altri lavoretti',
    leaveAReview: 'Lascia una recensione',
    jobsComingUp: 'Prossimi impegni',
    startsSoon: 'Inizia entro 24 ore',
    cannotCancel: 'Non annullabile',
    cancelThisJob: 'Annulla questo lavoretto',
    doNotFeelSafe: 'Non mi sento al sicuro',
    messageName: 'Scrivi a {name}',
    standingBy: 'Sei in riserva. Se chi è stato scelto rinuncia, il lavoretto passa a te.',
    stepDown: 'Rinuncia alla riserva',
    withdraw: 'Ritira',
    stepDownQ: 'Vuoi rinunciare alla riserva?',
    cancelJobQ: 'Vuoi annullare questo lavoretto?',
    keepIt: 'Lascia stare',
    cancellingRecorded: 'Le rinunce restano registrate sul tuo profilo. Cerca di non farne un’abitudine: è l’affidabilità che ti fa avere il lavoretto successivo.',
    noBackup: 'Nessuna riserva',
    postAChore: 'Pubblica un lavoretto',
  },

  offers: {
    title: 'Studenti disponibili',
    subtitle: 'Gli studenti che hanno indicato quando sono liberi. Chiedi a uno di loro di un orario che ti va bene: niente è prenotato finché non accettano.',
    whatDoYouNeed: 'Di cosa hai bisogno?',
    anything: 'Qualsiasi cosa',
    loading: 'Cerco studenti disponibili...',
    emptyTitle: 'Al momento non c’è nessuno disponibile',
    emptyBody: 'Nessuno studente ha pubblicato una disponibilità che corrisponda. Puoi invece pubblicare un lavoretto e lasciare che siano loro a candidarsi.',
    town: 'Comune',
    seeTheirTimes: 'Vedi i suoi orari',
    nextFree: 'Prossima disponibilità',
    timesAvailable: '{count} orari disponibili',
    // "studente" al singolare come in directory.count: è la scelta già fatta
    // altrove sul sito. "disponibile" non ha genere, quindi la frase va bene
    // sia per un ragazzo sia per una ragazza - vedi la decisione 3 in cima.
    countFree: { one: '{count} studente disponibile', other: '{count} studenti disponibili' },
    worksIn: 'Lavora a {city}',
    // "svolto" concorda con "lavoretto", non con la persona: nessun genere.
    jobsDone: { one: '{count} lavoretto svolto', other: '{count} lavoretti svolti' },
  },

  // "disponibili", non "pronti": "pronti" è maschile, "disponibile" non ha
  // genere - la decisione 3 in cima. Le chiavi di `readyFor` sono le categorie
  // salvate nel database, come in `categories`: non vanno mai cambiate.
  availableStudents: {
    title: 'Studenti disponibili',
    subtitle: 'Quanti studenti possono fare un lavoretto adesso, e cosa se la sentono di fare.',
    loading: 'Conteggio degli studenti...',
    readyTotal: 'Studenti disponibili a fare lavoretti',
    byKind: 'Cosa se la sentono di fare',
    readyFor: {
      Cleaning: 'Studenti disponibili per le pulizie',
      Gardening: 'Studenti disponibili per il giardinaggio',
      Babysitting: 'Studenti disponibili a fare da babysitter',
      'Pet Care': 'Studenti disponibili a occuparsi di animali',
      Tutoring: 'Studenti disponibili a dare ripetizioni',
      'Shopping & Errands': 'Studenti disponibili per spesa e commissioni',
      'Moving & Lifting': 'Studenti disponibili per traslochi e trasporti',
      'Tech Help': 'Studenti disponibili ad aiutare con la tecnologia',
      'DIY & Repairs': 'Studenti disponibili per fai da te e riparazioni',
      Other: 'Studenti disponibili per altri lavoretti',
    },
    choresCompleted: 'Lavoretti completati finora su ChoreConnect',
    onlyModerators: 'Per ora questo numero lo vedono solo i moderatori.',
    ctaBody: 'Pubblica un lavoretto: gli studenti disponibili potranno candidarsi.',
  },


  choreDetail: {
    startsSoonTitle: 'Questo lavoretto inizia a breve.',
    startsSoonBody: 'Inizia {when}. Puoi comunque annullare, ma dovrai dire a {name} il perché: leggerà esattamente quello che scrivi.',
    whyCancelling: 'Perché non puoi venire?',
    whyCancellingHint: 'Questo lavoretto inizia a breve, quindi {name} deve saperlo. Basta una frase: leggerà esattamente quello che scrivi.',
    whyCancellingPlaceholder: 'Sto male e non riesco ad arrivare. Scusa per il poco preavviso.',
    whyCancellingShort: 'Scrivi ancora un po’: è quello che leggeranno.',
    loading: 'Carico il lavoretto...',
    backToMarketplace: 'Torna alla bacheca',
    notFound: 'Non abbiamo trovato questo lavoretto.',
    whatNeedsDoing: 'Cosa c’è da fare',
    address: 'Indirizzo:',
    whoIsDoing: 'Chi se ne occupa',
    doingThis: 'Fa questo lavoretto',
    reviews: 'Recensioni',
    yourTurn: 'Tocca a te recensire',
    noReviews: 'Ancora nessuna recensione.',
    payment: 'Compenso',
    yourApplication: 'La tua candidatura',
    noLongerTaking: 'Questo lavoretto non accetta più candidature.',
    postedBy: 'Pubblicato da',
    addMessage: 'Aggiungi un messaggio (facoltativo)',
    apply: 'Candidati a questo lavoretto',
    applyBackup: 'Candidati come riserva',
    sending: 'Invio in corso...',
    sendApplication: 'Invia la candidatura',
    applicationSent: 'Candidatura inviata. Riceverai una risposta tramite le notifiche.',
    applicationWithdrawn: 'Candidatura ritirata.',
    reviewSaved: 'Grazie, la tua recensione è stata salvata.',
    datesMovedTitle: 'Chi ha pubblicato il lavoretto ha cambiato quando si svolge',
    datesMovedBody:
      'Avevi accettato date diverse, quindi puoi annullare questo lavoretto fino ' +
      'all’ultimo se le nuove non ti vanno bene. Non verrà conteggiato a tuo ' +
      'carico e nessuno penserà male di te.',
    stepDown: 'Rinuncia a fare da riserva',
    cancelJob: 'Cancella questo lavoro',
    stepDownQ: 'Rinunciare a fare da riserva?',
    cancelJobQ: 'Cancellare questo lavoro?',
    cancelling: 'Cancellazione in corso...',
    yesCancel: 'Sì, cancella',
    removedBackup: 'Verrai tolto dalla riserva per questo lavoretto.',
    removedJob: 'Verrai tolto da questo lavoretto e chi lo ha pubblicato ne sarà informato.',
    removedNotice: 'Sei stato tolto da questo lavoretto. È tornato in bacheca.',
    youAreBackup: 'Sei la riserva. Se lo studente principale rinuncia, questo lavoretto diventa automaticamente tuo.',
    jobIsYours: 'Questo lavoretto è tuo. Presentati puntuale e controlla l’indirizzo qui sopra.',
    jobIsYoursNoAddress: 'Questo lavoretto è tuo. Manca ancora l’indirizzo: scrivi a {name} per sapere dove andare.',
    askAboutJob: 'Fai una domanda su questo lavoretto',
    messageName: 'Scrivi a {name}',
    noAddressOwner: 'Su questo lavoretto manca l’indirizzo: senza, chi lo fa non sa dove andare.',
    addAddress: 'Aggiungi l’indirizzo',
    noAddressStudent: '{name} non ha ancora aggiunto l’indirizzo. Manda un messaggio per chiedere dove andare.',

    theDates: 'Le date',
    eachTimeTotal: '{each} ogni volta, {total} in tutto.',
    cancelThisDay: 'Cancella questa giornata',
    cannotMakeDay: 'Questa giornata non ce la faccio',
    dayCancelled: 'Quella giornata è stata cancellata. Il resto della prenotazione resta invariato.',
    cancelDayQ: 'Cancellare solo questa giornata?',
    yesCancelDay: 'Sì, cancella questa giornata',
    whyOptional: 'Perché? (facoltativo)',
    whyPosterPlaceholder: 'Quel giorno siamo via',
    whyStudentPlaceholder: 'Quel pomeriggio ho un compito in classe',
    whyToStudent: 'Viene riferito allo studente, con le tue parole.',
    whyToPoster: 'Viene riferito a chi ha pubblicato. Una breve spiegazione gli rende molto più facile organizzarsi.',
    noBackupChosen: 'Nessuna riserva scelta',
    visitCancelled: ' - annullata',
    visitDone: ' - fatta',
    dropSingleDay: 'Puoi rinunciare a una singola giornata fino a 24 ore prima. Se qualcosa non va proprio quel giorno, o non ti senti al sicuro ad andarci, usa invece il pulsante “Non mi sento al sicuro”: funziona sempre e non viene mai conteggiato a tuo sfavore.',
    sharedBecauseInvolved: 'Condiviso con te perché partecipi a questo lavoretto.',
    backupBadge: 'Riserva',
    takingBackups: 'Qualcuno è già stato preso per questo lavoretto. Fare la riserva significa subentrare in automatico se rinuncia con più di 24 ore di anticipo, e in ogni caso ti verrà detto.',
    withdrawApplication: 'Ritira la candidatura',
    onlyStudentsApply: 'Solo gli account studente possono candidarsi ai lavoretti.',
    signUpToApply: 'Registrati per candidarti',
    alreadyHaveAccount: 'Hai già un account? Accedi',
    applyPlaceholder: 'Spiega perché saresti la persona giusta: esperienze fatte e quando hai tempo.',
    keepIt: 'Lascia stare',
    cancellingShows: 'La rinuncia resta registrata sul tuo profilo. Se c’è una riserva subentrerà in automatico, altrimenti il lavoretto torna tra gli annunci.',
  },


  reportIssue: {
    title: 'Segnala un problema',
    subtitle:
      'Qualcosa sul sito non funziona? Scrivilo qui. Arriva direttamente a chi si occupa di ChoreConnect.',
    listHint: 'Qualcosa non funziona? Raccontacelo.',
    notForPeople:
      'Questo è per il sito in sé: un pulsante che non fa niente, una pagina che non si carica. Se qualcuno ti ha messo a disagio, usa invece il pulsante per segnalare sul suo profilo, oppure lascia un lavoretto in cui non ti senti al sicuro. Arrivano alle stesse persone, più in fretta.',
    empty: 'Ancora niente. Descrivi cosa è andato storto e cosa stavi cercando di fare, e ce ne occupiamo.',
    placeholder: 'Cosa stavi facendo, e cosa è successo?',
    moderator: 'Moderatore',
    send: 'Invia',
    yourMessage: 'Il tuo messaggio',
  },

  emailOffline: {
    banner:
      'Al momento non riusciamo a inviare email, quindi iscriversi, confermare un indirizzo e reimpostare la password non sono disponibili. È temporaneo e di solito si risolve entro un giorno.',
  },

  resend: {
    sentTitle: 'Email inviata',
    sentBody: 'È in arrivo a {email}. Di solito arriva entro un minuto: se non arriva, controlla la posta indesiderata prima di chiederne un’altra.',
    waitFor: 'Puoi inviarne un’altra tra {time}',
  },

  createChore: {
    flexibleLabel: 'Sono flessibile sull’orario',
    flexibleHint: 'Spunta questa casella se conta il giorno ma non l’ora. Gli studenti vedranno “a un orario che va bene a entrambi”, e potrete concordare l’ora tra voi una volta che qualcuno ha preso il lavoretto.',
    titleNew: 'Pubblica un lavoretto',
    titleEdit: 'Modifica il lavoretto',
    lockedTitle: 'Uno studente ha già accettato questo lavoretto',
    lockedBody:
      'Puoi ancora cambiare il giorno e l’ora, e glielo comunicheremo. ' +
      'Il resto adesso è fissato: ha detto di sì al lavoretto così com’è. ' +
      'Se il lavoro è cambiato davvero, annulla questo e pubblicane uno nuovo.',
    addMissingAddress:
      'Una cosa si può ancora aggiungere: l’indirizzo. Su questo lavoretto manca, ' +
      'e senza non si sa dove venire. Il campo è in fondo al modulo.',
    lockedDatesWarning:
      'Se cambi il giorno o l’ora, questo studente può annullare senza preavviso ' +
      'e la cosa non verrà conteggiata a suo carico. Se puoi, spiegagli il perché.',
    lede: 'Più è chiaro cosa comporta, migliori saranno le candidature che riceverai.',
    needMembership: 'Ti serve un abbonamento attivo per pubblicare lavoretti.',
    membershipBody: 'ChoreConnect costa 5 € all’anno per chi pubblica lavoretti, ed è sempre gratis per gli studenti.',
    seeMembership: 'Vedi le opzioni di abbonamento',
    fieldTitle: 'Titolo',
    titlePlaceholder: 'Tagliare il prato e sistemare la siepe',
    whatNeedsDoing: 'Cosa c’è da fare?',
    descriptionPlaceholder: 'Descrivi il lavoro, quanto tempo richiede all’incirca, e tutto quello che lo studente deve portare o sapere. Indica se l’attrezzatura la metti tu.',
    descriptionHint: '{count}/2000 - almeno 20 caratteri',
    category: 'Categoria',
    payment: 'Compenso (€)',
    paymentPerVisit: 'Compenso per volta (€)',
    paySum: '{each} × {count} = {total}',
    whereIsIt: 'Dove si svolge?',
    postIt: 'Pubblica il lavoretto',
    saveChanges: 'Salva le modifiche',

    whenTitle: 'Quando serve?',
    whenLegend: 'Ogni quanto serve?',
    modeOne: 'Un solo giorno',
    modeOneHint: 'Un lavoro singolo, a un orario preciso',
    modeDates: 'Più giorni',
    modeDatesHint: 'Scegli i giorni sul calendario',
    modeWeekly: 'Ogni settimana',
    modeWeeklyHint: 'Gli stessi giorni ogni settimana, fino a una data che scegli tu',
    whatTime: 'A che ora?',
    whatTimeEach: 'A che ora ogni giorno?',
    howLong: 'Quanto dura all’incirca? (ore)',
    howLongEach: 'Quanto dura ogni volta? (ore)',
    whichDay: 'Quale giorno?',
    whichDays: 'Quali giorni?',
    whichDaysHint: 'Tocca ogni giorno in cui ti serve qualcuno. Non devono essere di fila: salta i giorni che hai già coperto.',
    whichWeekdays: 'Quali giorni della settimana?',
    until: 'Continua fino al',
    untilHint: 'Tocca l’ultimo giorno in cui deve andare avanti. Fino a {months} mesi da oggi: puoi sempre annullare o modificare in seguito.',
    runsUntil: 'Va avanti fino a',
    visitCount: { one: '{count} volta', other: '{count} volte' },
    eachAndTotal: '{each} ogni volta · {total} in tutto',
    first: 'Prima: {date}',
    last: 'Ultima: {date}',
    seeEveryDate: 'Vedi tutte le date',
    overLimit: 'Sono più di {max} appuntamenti, il massimo che una prenotazione può contenere. Scegli una data di fine più vicina, o meno giorni a settimana.',
    pickWeekly: 'Scegli almeno un giorno della settimana e una data fino a cui andare avanti.',
    pickDays: 'Scegli almeno un giorno sul calendario.',
    mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Gio', fri: 'Ven', sat: 'Sab', sun: 'Dom',
    fiveAYear: 'ChoreConnect costa 5 € all’anno per chi pubblica lavoretti, ed è sempre gratuito per gli studenti.',
    subtitle: 'Più è chiaro cosa comporta, migliori saranno le candidature che riceverai.',
  },


  profile: {
    loading: 'Carico il profilo...',
    posterNotFound: 'Non abbiamo trovato questa persona.',
    studentNotFound: 'Non abbiamo trovato questo studente.',
    atAGlance: 'In sintesi',
    choresCompletedByStudents: 'Lavoretti completati dagli studenti',
    openRightNow: 'Aperti in questo momento',
    reviewsFromStudents: 'Recensioni dagli studenti',
    choresOpenNow: 'Lavoretti aperti adesso',
    trackRecord: 'Storico',
    reviewsReceived: 'Recensioni ricevute',
    happyToDo: 'Disponibile per',
    recentWork: 'Lavori recenti',
    noReviews: 'Ancora nessuna recensione.',
    editMyProfile: 'Modifica il mio profilo',
    choresCompleted: 'Lavoretti completati',
    joined: 'Iscritto dal {date}',
    reviewsWithCount: 'Recensioni ({count})',
    noReviewsBody: 'Ancora nessuna recensione. Si comincia sempre da zero: il primo lavoretto è quello difficile.',
  },

  thread: {
    loading: 'Carico la conversazione...',
    none: 'Qui non c’è nessuna conversazione.',
    backToMessages: 'Torna ai messaggi',
    yourMessage: 'Il tuo messaggio',
    send: 'Invia',
    archive: 'Archivia questa conversazione',
    theyAreAsking: '{name} sta facendo una domanda su questo lavoretto. Non si è candidato e non c’è nessuna prenotazione.',
    askingAbout: 'Stai facendo una domanda su questo lavoretto. Non ti sei candidato e non c’è nessuna prenotazione: l’indirizzo viene condiviso solo se accetti il lavoro.',
    closedTaken: 'Questo lavoretto è stato assegnato, quindi la conversazione è ora in sola lettura.',

    /* ---- Una domanda sulla disponibilità di uno studente. Qui è
       l’adulto a scrivere per primo, quindi le parole dicono chiaramente
       di chi è la scelta di rispondere. ---- */
    aboutTheirTimes: 'Sulla sua disponibilità',
    theyAskedAboutYourTimes: '{name} ha fatto una domanda sugli orari che hai pubblicato. Sta a te decidere se rispondere: senza una tua risposta non può scriverti altro.',
    youAskedAboutTheirTimes: 'Hai fatto una domanda sulla disponibilità di questo studente. Non c’è nessuna prenotazione, ed è lui a decidere se rispondere.',
    waitingForAnswer: 'Hai fatto la tua domanda. Potrai scrivere di nuovo quando {name} avrà risposto: è una sua scelta.',
    closedOfferGone: 'Questa disponibilità non è più pubblicata, quindi la conversazione è in sola lettura.',
    closedOffersOff: 'Le disponibilità non ci sono più su ChoreConnect, quindi questa conversazione è in sola lettura. Puoi comunque rileggerla e segnalarla.',
    keptNotice: 'I messaggi qui vengono conservati, e possono essere letti da un moderatore se qualcuno li segnala.',
    closedCancelled: 'Questo lavoretto è stato annullato, quindi la conversazione è chiusa. Puoi comunque rileggerla.',
    closedFinished: 'Questo lavoretto è finito da un po’, quindi la conversazione è in sola lettura. Puoi comunque rileggerla.',
    noMessagesYet: 'Ancora nessun messaggio. Salutate, o chiedete quello che vi serve sapere sul lavoretto.',
    phoneWarning: 'Sembra un numero di telefono o un’email. Puoi comunque inviarlo, ma tenere la conversazione qui significa che resta una traccia se qualcosa dovesse andare storto.',
    messagePlaceholder: 'Messaggio...',
  },

  confirmBanner: {
    title: 'Conferma il tuo indirizzo email',
    body: 'Ti abbiamo mandato un link a {email} quando hai creato l’account. Puoi guardarti intorno finché non lo apri, ma non puoi pubblicare un lavoretto, candidarti, né organizzare niente con nessuno.',
    resend: 'Rimanda l’email',
    sending: 'Invio in corso...',
    spamHint: 'Controlla la cartella spam: di solito è finita lì.',
  },

  guardianBanner: {
    titleWaiting: 'In attesa di un genitore o tutore',
    titleRenewal: 'Dobbiamo chiedere di nuovo al tuo genitore',
    theirName: 'Il loro nome',
    theirEmail: 'Il loro indirizzo email',
    resend: 'Rimanda l’email',
    yourGuardian: 'il tuo genitore o tutore',
    renewalBefore: 'ChoreConnect è cambiato da quando',
    renewalAfter: 'ha dato il consenso: ora puoi indicare quando hai tempo, scrivere a chi ti affida un lavoretto e ricevere avvisi sul telefono. Abbiamo chiesto di guardare di nuovo. Nel frattempo puoi consultare i lavoretti, ma non puoi candidarti né essere prenotato finché non arriva una risposta.',
    waitingBefore: 'Puoi consultare i lavoretti e preparare il tuo profilo, ma non puoi candidarti finché non conferma',
    waitingAfter: '. Gli abbiamo inviato un link quando hai creato l’account.',
    saveAndSend: 'Salva e invia',
    wrongAddress: 'Indirizzo sbagliato?',
    spamHint: 'Chiedi di controllare la cartella spam: di solito è finita lì.',
  },


  offerDetail: {
    howLong: 'Per quante ore?',
    // "si sposta" e "disponibile" non hanno genere - decisione 3 in cima.
    worksInUpTo: 'Lavora a {city}, si sposta fino a {km}km',
    worksInAnywhere: 'Lavora a {city}, disponibile a spostarsi',
    pickDaysHint: 'Scegli i giorni in cui ti serve {name}, poi l’orario e per quanto tempo. Più giorni vanno come un’unica richiesta a cui risponde una volta sola.',
    tooEarly: 'Scegli un orario dalle 07:00 in poi. Non si può chiedere a uno studente di lavorare di notte.',
    tooMany: 'Sono più di {max} giorni in una sola richiesta. Manda gli altri separatamente.',
    // "libero" dava per scontato che fosse un ragazzo. "disponibile" vale per
    // tutti e due - decisione 3 in cima al file. Corretto il 25 agosto 2026,
    // quando questa frase è diventata l'etichetta del menu a tendina.
    usuallyFree: 'Quando {name} dice di essere di solito disponibile',
    whatTime: 'A che ora?',
    whenDoYouNeedThem: 'Quando ti serve?',
    whichDays: 'Quali giorni?',
    loading: 'Caricamento...',
    notFound: 'Non abbiamo trovato questa disponibilità.',
    back: 'Torna agli studenti disponibili',
    pickHint: 'Scegli un orario, oppure più di uno. Se ne scegli diversi partono come un’unica richiesta a cui {name} risponde una volta sola, così non ti ritrovi con due giorni su tre.',
    alreadyAsked: 'Richiesta inviata - in attesa di risposta',
    chosen: 'Scelto',
    chooseThis: 'Scegli questo orario',
    timesChosen: { one: '{count} orario scelto', other: '{count} orari scelti' },
    askAboutOne: 'Chiedi di questo orario',
    askAboutMany: 'Chiedi di questi {count} orari',

    /* ---- Chiedere qualcosa prima di proporre un orario ---- */
    questionTitle: 'C’è qualcosa che devi sapere prima?',
    questionBody: 'Puoi fare una domanda a {name} prima di proporre un orario: se se la cava con i cani, quanto lontano si sposta, qualsiasi cosa sia decisiva.',
    questionWaiting: 'Hai fatto la tua domanda. Sta a {name} decidere se rispondere.',
    askAboutOffer: 'Chiedi informazioni su questa disponibilità',
    seeConversation: 'Vai alla conversazione',
    sendQuestion: 'Invia la domanda',
    sendingQuestion: 'Invio...',
    questionRules: 'Una domanda. {name} sceglie se rispondere, e senza una sua risposta non puoi scrivere altro. Qui non si prenota nulla.',
    yourQuestion: 'La tua domanda',
    questionPlaceholder: 'Breve e sul lavoro: «è un problema se c’è un cane grande?», «riusciresti alle 18 invece che alle 17?»',
    askTitleOne: 'Chiedi a {name} di {when}',
    askTitleMany: 'Chiedi a {name} di {count} orari',
    sending: 'Invio in corso...',
    sendRequest: 'Invia la richiesta',
    whatIsJob: 'Di che lavoretto si tratta?',
    jobPlaceholder: 'Babysitting per la serata',
    payOne: 'Quanto paghi? (€)',
    payEach: 'Quanto paghi ogni volta? (€)',
    paySum: '{each} × {count} volte = {total} in tutto',
    payHint: '{name} lo vede prima di decidere. Il denaro passa direttamente tra voi due: ChoreConnect non lo gestisce mai.',
    address: 'Indirizzo',
    addressPlaceholder: 'Via Piave 14, secondo piano',
    addressHint: 'Condiviso solo se accetta. Fino ad allora vede soltanto il comune.',
    anythingElse: 'C’è qualcosa che dovrebbe sapere?',
    anythingPlaceholder: 'Due bambini, di 6 e 9 anni. Avranno già cenato, a letto alle 20.',
  },

  manage: {
    loading: 'Carico le candidature...',
    ageYears: 'Età {age}',
    notFound: 'Non abbiamo trovato questo lavoretto.',
    back: 'Torna ai miei lavoretti',
    markComplete: 'Segna come completato',
    completed: 'Segnato come completato.',
    reviewHeading: 'Lascia una recensione',
    reviewNotNow: 'Non ora',
    reviewSaved: 'Grazie. La tua recensione è stata salvata.',
    cancelled: 'Lavoretto annullato. Tutti i coinvolti sono stati avvisati.',
    considerBackup: 'Valuta di indicare una riserva.',
    noApplications: 'Ancora nessuna candidatura',
    noApplicationsBody: 'Gli studenti della zona vedranno questo lavoretto in bacheca. Dagli un giorno o due, e controlla che il compenso sia realistico per il lavoro richiesto.',
    removeBackup: 'Togli dalla riserva',
    turnDown: 'Rifiuta',
    completeQ: 'Segnare questo lavoretto come completato?',
    cancelQ: 'Annullare questo lavoretto?',
    cancelReason: 'Motivo (facoltativo, viene condiviso con loro)',
    cancelPlaceholder: 'Cambio di programma, non serve più...',
    deleteQ: 'Eliminare questo lavoretto?',
    deleteBody: 'Questo elimina del tutto il lavoretto. Non si può annullare.',
    noBackupChosen: 'Nessuna riserva scelta',
    viewPublicPage: 'Vedi la pagina pubblica',
    deleteChore: 'Elimina',
    cancelChore: 'Annulla il lavoretto',
    considerBackupBody: 'Se lo studente scelto deve rinunciare, la riserva subentra in automatico invece di far saltare il lavoretto.',
    accept: 'Accetta',
    makeBackup: 'Rendi riserva',
    notYet: 'Non ancora',
    yesItIsDone: 'Sì, è finito',
    keepIt: 'Lascia stare',
    cancelTheChore: 'Annulla il lavoretto',
    cancelBody: 'Verrà comunicato a tutti quelli che si sono candidati, e lo studente eventualmente prenotato sarà liberato.',
    deletePermanently: 'Elimina definitivamente',
  },


  withdraw: {
    invalid: 'Questo link non è valido.',
    title: 'Revoca il tuo consenso',
    forWhom: 'Per {name}',
    whatHappens: 'Cosa succede se lo fai',
    effect1: 'Non potrà più candidarsi ai lavoretti.',
    effect2: 'Non potrà più pubblicare quando è disponibile, e nessuno potrà prenotarlo.',
    effect3: 'Potrà comunque accedere e guardarsi intorno, e i lavori già concordati restano validi.',
    effect4: 'Il suo account non viene eliminato, e nemmeno le sue recensioni.',
    doneTitle: 'Fatto, ed è già efficace.',
    doneBody: '{name} non può più candidarsi, pubblicare quando è disponibile, né ricevere richieste da nessuno.',
    doneCopy: 'Le abbiamo inviato una copia per i Suoi archivi. Il suo account esiste ancora e i lavori già concordati restano validi.',
    nothingTitle: 'Non c’è nulla da revocare.',
    nothingBody: '{name} al momento non ha il Suo consenso, quindi già non può candidarsi né essere prenotato.',
    deleteInstead: 'Se preferisce che l’account venga eliminato del tutto',
    deleteInsteadBody: ', risponda a una qualsiasi nostra email e ce lo dica. È una cosa diversa e non la faremo per sbaglio.',
    replyToWithdraw: 'Se stavi cercando di ritirare il consenso, rispondi a una qualsiasi email che ti abbiamo mandato e se ne occuperà una persona.',
    deleteAltogether: 'Il loro account resta attivo e i lavoretti già concordati restano validi. Se preferisci che venga eliminato del tutto, rispondi a quella email e lo faremo.',
    canAskAgain: '{name} può richiedertelo di nuovo dal proprio account, e arriverà una nuova richiesta a questo indirizzo. Non succede nulla senza il tuo consenso, e ignorarla lascia tutto esattamente com’è adesso.',
    aboutChoreConnect: 'Che cos’è ChoreConnect',
    nothingWhy: 'Può essere perché lo hai già ritirato, oppure perché abbiamo cambiato ciò che chiediamo ai genitori e stiamo aspettando una nuova risposta. In ogni caso, non succede nulla senza di te.',
    agreedOn: ', consenso dato il {date}',
    saving: 'Salvataggio...',
    yesWithdraw: 'Sì, ritiro il consenso per {name}',
    noLeave: 'No, lascia tutto com’è',
  },

  ban: {
    closedTitle: 'Il tuo account è stato chiuso',
    suspendedTitle: 'Il tuo account è sospeso',
    permanentBody: 'Non è temporaneo. Se pensi che sia un errore, rispondi a una qualsiasi email che ti abbiamo mandato, oppure scrivi a',
    mistakeBody: 'Se pensi che sia un errore, scrivi a',
    over: 'La tua sospensione è finita. Ora puoi accedere.',
    days: { one: '{count} giorno', other: '{count} giorni' },
    hours: { one: '{count} ora', other: '{count} ore' },
    minutes: { one: '{count} minuto', other: '{count} minuti' },
    and: ' e ',
    listJoin: ', ',
    lessThanMinute: 'meno di un minuto',
    timeLeft: 'Ancora {time}',
    canLogInAfter: 'Dopo potrai accedere di nuovo, senza dover fare altro.',
    backHome: 'Torna alla pagina iniziale',
  },

  leave: {
    title: 'Lascia questo lavoretto',
    reportTitle: 'Avvisa un moderatore',
    sending: 'Invio...',
    aboutThisJob: 'Si tratta di',
    overNowBody: 'Il lavoretto è concluso, quindi non c’è nulla da lasciare, ma se è successo qualcosa mentre eri lì è giusto che qualcuno lo sappia.',
    notToldDoneBody: 'E nulla del lavoretto cambia: resta esattamente com’è.',
    leaving: 'Uscita in corso...',
    emergency: 'Se sei in pericolo adesso, chiama il 112.',
    emergencyBody: 'Fallo per primo. Questo può aspettare.',
    noObligation: 'Non devi finire il lavoretto e non devi restare.',
    noObligationBody: 'Sei fuori nel momento in cui premi il pulsante: non dipende da nessuno che legga quello che scrivi.',
    noCount: 'Non verrà conteggiato a tuo carico.',
    noCountBody: 'Non è un annullamento e non comparirà sul tuo profilo.',
    notTold: 'Non gli verrà detto che hai fatto una segnalazione.',
    notToldBody: 'Sanno solo che il lavoretto è di nuovo libero.',
    readAtEight: 'Un moderatore lo legge alle 20:00.',
    readAtEightBody: 'Le segnalazioni vengono lette una volta al giorno, a quell’ora - di notte non c’è nessuno che controlla il sito.',
    whatHappened: 'Che cosa è successo?',
    whatHappenedHint: 'Qualche frase. Dov’eri, cosa è stato detto o fatto, e cosa ti ha fatto venire voglia di andartene. Un moderatore lo legge alle 20:00 ed è tutto quello che ha per capire.',
    onlyModerator: 'Lo legge solo un moderatore. Non viene mostrato a chi ha pubblicato il lavoretto.',
    misuseTitle: 'Questo serve se non ti senti al sicuro, non se hai cambiato idea.',
    misuseBody: 'Usarlo per uscire da un lavoretto che semplicemente non ti va è una violazione dei termini di servizio e può portare alla sospensione del tuo account. Se non puoi proprio venire, annulla il lavoretto e spiega perché: è a questo che serve.',
    goBack: 'Torna indietro',
    youAreLeaving: 'Stai lasciando',
    thisJob: 'questo lavoretto',
    reasonPlaceholder: 'Che cosa è successo? Anche poche righe aiutano a capire.',
    tooShort: 'Scrivi ancora un po’: è quello su cui deve basarsi un moderatore.',
  },


  myOffer: {
    addAnother: 'Aggiungi un altro annuncio',
    editOffer: 'Modifica questo annuncio',
    noneYet: 'Non hai ancora pubblicato niente. Un annuncio serve a farti trovare, invece di dover cercare tu.',
    removeWindow: 'Togli dalle {from} alle {to} di {day}',
    takenDownBadge: 'Rimosso',
    takenDownTitle: 'Un moderatore lo ha rimosso.',
    weekIsShared: 'I tuoi orari si impostano una volta sola e valgono per tutti i tuoi annunci: qui non li reimposti.',
    weekSaved: 'La tua settimana è stata salvata.',
    whichTown: 'In quale città',
    yourOffers: 'I tuoi annunci ({count} di {max})',
    loading: 'Carico la tua disponibilità...',
    title: 'La mia disponibilità',
    // "libero" dava del maschile a chi legge: una ragazza si vedeva scritto
    // "sei di solito libero". "disponibile" è uguale per tutti - decisione 3
    // in cima al file.
    subtitle: 'Indica cosa sai fare e quando sei di solito disponibile, così possono venire loro da te.',
    yours: 'La tua disponibilità',
    create: 'Crea la tua disponibilità',
    whatCanYouDo: 'In cosa puoi dare una mano?',
    pickFive: 'Scegline fino a cinque.',
    aboutYou: 'Due parole su quello che fai',
    aboutPlaceholder: 'Faccio da babysitter ai vicini quasi tutti i fine settimana e me la cavo bene con i bambini piccoli...',
    atLeastChars: 'almeno {count} caratteri',
    town: 'Comune in cui puoi lavorare',
    howFar: 'Quanto lontano puoi spostarti?',
    doNotMind: 'Non ho preferenze',
    upToKm: 'Fino a {km} km',
    availableOn: 'Disponibile:',
    nothingSet: 'Non hai ancora impostato niente.',
    publish: 'Pubblica la mia disponibilità',
    saveChanges: 'Salva le modifiche',
    takeDownQ: 'Vuoi togliere la tua disponibilità?',
    saved: 'Salvato. Da adesso questa è la tua settimana, finché non la cambi.',
    accepted: 'Accettato. Ora è tra i tuoi lavori, con l’indirizzo.',
    declined: 'Rifiutato. Gliel’abbiamo detto, senza spiegare perché.',
    takenDown: 'La tua disponibilità è stata tolta. Quello che era già stato concordato resta valido.',
    fillBoth: 'Compila entrambi gli orari.',
    finishAfterStart: 'L’orario di fine deve essere dopo quello di inizio.',
    underMinutes: 'Sono meno di {count} minuti.',
    longerThanHours: 'Sono più di {count} ore.',
    overlaps: 'Si sovrappone a {from}-{to}.',
    askedToBookYou: { one: '{count} persona ti ha chiesto di prenotarti', other: '{count} persone ti hanno chiesto di prenotarti' },
    nothingBooked: 'Non viene prenotato nulla finché non accetti. Non devi dare spiegazioni se rifiuti.',
    separateTimes: '{count} volte separate',
    eachAmount: '{amount} ciascuna',
    inAll: '{amount} in totale',
    until: 'fino alle {time}',
    yesAgreesAll: 'Accettando li accetti tutti. Se ti vanno bene quasi tutti tranne uno, rifiuta e diglielo: potranno richiedere solo i giorni che ti servono. Una volta prenotato puoi comunque rinunciare a una singola giornata, fino a 24 ore prima.',
    fullAddressOnAccept: 'Riceverai l’indirizzo completo solo dopo aver accettato.',
    accept: 'Accetta',
    noThanks: 'No, grazie',
    live: 'Attiva',
    paused: 'In pausa',
    pause: 'Metti in pausa',
    resume: 'Riattiva',
    notAnAgreement: 'Inserire degli orari qui non impegna a nulla. Se qualcuno ne vuole uno deve prima chiedertelo, e vedrai di che lavoretto si tratta e quanto paga prima di decidere.',
    noPersonalDetails: 'Non scrivere qui il tuo indirizzo, il numero di telefono o la classe: sono visibili a chiunque possa vedere la tua disponibilità.',
    whenUsuallyFree: 'Quando sei di solito disponibile?',
    startTimeOn: 'Ora di inizio di {day}',
    finishTimeOn: 'Ora di fine di {day}',
    from: 'Dalle',
    to: 'Alle',
    toWord: 'alle',
    add: 'Aggiungi',
    saveMyWeek: 'Salva la mia settimana',
    resetToUsual: 'Torna alla settimana tipo',
    takeItDown: 'Rimuovi la disponibilità',
    keepItUp: 'Lasciala attiva',
    takeDownBody: 'Smetterai di comparire quando qualcuno cerca chi è libero. Tutto ciò che hai già accettato resta valido: quei lavoretti restano nei tuoi impegni e non cambiano.',
  },

  report: {
    button: 'Segnala',
    emergency: 'Se qualcuno è in pericolo immediato, chiama prima il 112.',
    emergencyBody: 'Poi dillo a noi, così possiamo intervenire.',
    whatHappened: 'Cosa è successo?',
    chooseOne: 'Scegli una voce...',
    anythingToAdd: 'Vuoi aggiungere qualcosa?',
    choosePlease: 'Scegli cosa è successo.',
    r1: 'Mi ha fatto sentire a disagio o non al sicuro',
    r2: 'Comportamento o messaggi inappropriati',
    r3: 'Mi è stato chiesto di fare qualcosa di pericoloso',
    r4: 'Non ha pagato quanto concordato',
    r5: 'Mancata presentazione all’appuntamento',
    r6: 'Il profilo non sembra autentico',
    r7: 'Una recensione offensiva o ingiusta',
    r8: 'Altro',
    thisPerson: 'questa persona',
    reportName: 'Segnala {name}',
    blockButton: 'Blocca',
    blockName: 'Blocca {name}',
    blockNameQ: 'Vuoi bloccare {name}?',
    thankYou: 'Grazie',
    close: 'Chiudi',
    sending: 'Invio in corso...',
    send: 'Invia la segnalazione',
    detailsPlaceholder: 'Non è obbligatorio scrivere nulla, ma ci aiuta a capire.',
    realPerson: 'Ogni segnalazione viene letta da una persona vera. Non diremo a nessuno chi l’ha inviata.',
    blocked: 'Persona bloccata',
    blocking: 'Blocco in corso...',
    blockThem: 'Blocca',
    blockBody: 'Non vedrai più {name} né i suoi lavoretti, e non potrà vedere te. Non devi dare spiegazioni e non gliene verrà detto nulla.',
    blockUndo: 'Puoi annullare il blocco quando vuoi dalle impostazioni del tuo account. Se è successo qualcosa che dovremmo sapere, segnalalo anche a noi.',
  },


  daypicker: {
    previousMonth: 'Mese precedente',
    nextMonth: 'Mese successivo',
    chooseDays: 'Scegli i giorni',
  },


  settingsPage: {
    title: 'Il tuo account',
    dateOfBirth: 'Data di nascita',
    dateOfBirthIs: '{date}: hai {age} anni',
    dateOfBirthFixed: 'Gli altri vedono solo la tua età, che si aggiorna al compleanno. Se la data è sbagliata, scrivici da "Segnala un problema".',
    dateOfBirthAdd: 'Aggiungila una volta sola e la tua età si aggiornerà da sola al compleanno. Deve corrispondere all’età già presente sul tuo account.',
    viewProfile: 'Vedi il profilo pubblico',
    yourDetails: 'I tuoi dati',
    detailsSaved: 'I tuoi dati sono stati salvati.',
    changePhoto: 'Cambia foto',
    addPhoto: 'Aggiungi una foto',
    photoUpdated: 'Foto aggiornata.',
    photoRemoved: 'Foto rimossa.',
    photoFailed: 'Non siamo riusciti a caricare quella foto. Provane un’altra.',
    firstName: 'Nome',
    lastName: 'Cognome',
    phone: 'Numero di telefono',
    school: 'Scuola',
    schoolYear: 'Anno scolastico',
    aboutYou: 'Parlaci di te',
    aboutPlaceholder: 'Che cosa sai fare bene? Quando sei di solito disponibile?',
    happyToDo: 'Cosa te la senti di fare',
    howFar: 'Quanto lontano ti sposti?',
    doNotMind: 'Non ho preferenze',
    upToKm: 'Fino a {km} km',
    whereYouAre: 'Dove ti trovi',
    saveChanges: 'Salva le modifiche',
    membership: 'Abbonamento',
    active: 'Attivo',
    notActive: 'Non attivo',
    manage: 'Gestisci',
    join: 'Iscriviti a 5 €/anno',
    changePassword: 'Cambia password',
    currentPassword: 'Password attuale',
    newPassword: 'Nuova password',
    atLeast8: 'Almeno 8 caratteri.',
    updatePassword: 'Aggiorna la password',
    passwordUpdated: 'Password aggiornata.',
    blocked: 'Persone che hai bloccato',
    closeAccount: 'Chiudi il tuo account',
    closing: 'Chiusura in corso...',
    closeForever: 'Sì, chiudilo definitivamente',
    cannotUndo: 'Non si può annullare. Non potrai più accedere.',
    whatGoes: 'Cosa viene rimosso:',
    whatStays: 'Cosa resta:',
    confirmPassword: 'Inserisci la password per confermare',
    removePhoto: 'Rimuovi la foto',
    age: 'Età',
    travelHint: 'Lascia “Non ho preferenze” a meno che tu non abbia davvero un limite.',
    needMembership: 'Serve un’iscrizione attiva per pubblicare lavoretti.',
    noBlocks: 'Non hai bloccato nessuno. Se qualcuno ti mette a disagio puoi bloccarlo dal suo profilo, e non gli verrà mai detto.',
    unblock: 'Sblocca',
    closeAccountBody: 'Rimuove il tuo nome, la tua email e ogni altro dato personale, e non si può annullare.',
    closeMyAccount: 'Chiudi il mio account',
    closeAccountQ: 'Vuoi chiudere il tuo account?',
    keepMyAccount: 'Mantieni il mio account',
    whatStaysBody: 'le recensioni che hai scritto, le stelle che hai dato e i lavoretti che hai completato, tutti mostrati come “Utente eliminato” senza alcun collegamento a te. Li conserviamo perché le persone con cui hai lavorato non perdano lo storico delle recensioni. Non contengono nulla che ti identifichi.',
    openWillBeCancelled: 'Tutto ciò che è ancora in corso verrà annullato, e l’altra persona ne sarà informata.',
    photoNotUploading: 'La foto non si carica? Leggi qui',
    photoInICloudQ: 'Prima cosa, ed è la più probabile: la foto è su iCloud invece che su questo dispositivo?',
    photoSignIs: 'Il segnale è questo:',
    photoTapAndI: 'tocchi la foto che vuoi e sopra compare una ⓘ',
    photoSignBody: '- una lettera i dentro un cerchio, proprio sulla foto. È lo stesso simbolo che mostra l’app Foto quando un’immagine è su iCloud e non riesce a scaricarla. Il dispositivo riesce a mostrarti l’anteprima ma non a consegnare il file vero, quindi qui non arriva nulla e non succede nulla.',
    photoFixLabel: 'La soluzione:',
    photoFixBefore: 'controlla di essere sotto wi-fi o di avere campo: deve scaricare la foto per farlo. Poi apri l’app',
    photoAppName: 'Foto',
    photoFixAfter: ', tocca l’immagine che vuoi e aspetta che finisca di scaricarsi: passa da sfocata a nitida e la ⓘ sparisce. Torna qui e si caricherà subito.',
    photoBrowseBefore: 'Su iPhone o iPad viene proposto anche',
    photoBrowseName: 'Sfoglia',
    photoBrowseAfter: ', che apre File invece della libreria foto. Salvare prima la foto in File e sceglierla da lì evita del tutto la libreria foto.',
    backToDashboard: 'Torna alla mia bacheca',
    signedInAs: 'Hai effettuato l’accesso come {email}',
    addAPhoto: 'Aggiungi una foto',
    working: 'Attendi...',
  },

  marketplaceFilters: {
    payFrom: 'Paga da (€)',
    payUpTo: 'Paga fino a (€)',
    anyDistance: 'Qualsiasi distanza',
    searchPlaceholder: 'prato, portare a spasso il cane, ripetizioni...',
    any: 'Qualsiasi',
    within: 'Entro',
    search: 'Cerca',
    closestToMe: 'Più vicini a me',
    toSortByDistance: 'per ordinare per distanza e filtrare per km.',
    clearFilters: 'Azzera i filtri',
    addYourAddress: 'Aggiungi il tuo indirizzo',
    previous: 'Precedente',
    next: 'Successiva',
    pageOf: 'Pagina {page} di {pages}',
  },


  notifyPrompt: {
    titleStudent: 'Vuoi sapere quando esce un lavoretto?',
    titlePoster: 'Vuoi sapere quando qualcuno si candida?',
    notNow: 'Non adesso',
    justAMoment: 'Un attimo...',
    yesTellMe: 'Sì, avvisami',
    doneStudent: 'Riceverai una notifica quando viene pubblicato un lavoretto che fa per te. Puoi cambiare di cosa essere avvisato, o disattivarlo, nelle impostazioni.',
    donePoster: 'Riceverai una notifica quando qualcuno si candida a un tuo lavoretto. Puoi disattivarlo dalle impostazioni.',
    pitchStudent: 'I lavoretti vanno via in fretta, e di solito li prende chi lo viene a sapere per primo. Possiamo mandarti una notifica sul telefono appena ne esce uno adatto a te - sulla schermata di blocco, senza che tu debba controllare di continuo.',
    reassureStudent: 'Scegli tu di cosa essere avvisato, di notte non arriva niente, e puoi disattivarlo quando vuoi.',
    reassurePoster: 'Di notte non arriva niente, e puoi disattivarlo quando vuoi.',
    noEmail: 'Non ti scriveremo email per i lavoretti.',
    allSet: 'Tutto pronto',
    offBody: 'Nessun problema. Puoi attivare le notifiche quando vuoi dalle Impostazioni: non cambia nient’altro.',
    pitchPoster: 'Un lavoretto senza nessuno è quasi sempre un lavoretto in cui chi si è candidato per primo non ha mai ricevuto risposta. Possiamo avvisarti appena qualcuno si candida, così puoi accettarlo finché è ancora libero.',
    waitingOnParent: 'Puoi attivarle subito: gli avvisi partiranno appena il tuo genitore o tutore avrà confermato l’account.',
  },

  twoFactor2: {
    recoveryCodes: 'Codici di recupero',
    noneLeft: 'Non ti restano codici di recupero. Se la tua email dovesse diventare irraggiungibile non potresti più moderare.',
    fewLeft: { one: 'Ti resta solo {count} codice di recupero. Generane una nuova serie.', other: 'Ti restano solo {count} codici di recupero. Generane una nuova serie.' },
    yourPassword: 'La tua password',
    generating: 'Generazione in corso...',
    generateNew: 'Genera una nuova serie',
    oldStop: 'I codici che hai già smetteranno di funzionare.',
    title: 'Accesso in due passaggi',
    whyBefore: 'Poiché questo account può leggere le conversazioni segnalate, per accedere servono una password',
    and: 'e',
    whyAfter: 'un codice di sei cifre. Inviamo il codice a',
    eachTime: 'ogni volta.',
    lastLogin: 'Ultimo accesso in due passaggi: {date}. Se non riconosci questo accesso, cambia subito la password.',
    eachLogsInOnce: 'Ognuno permette un solo accesso, senza email. Sono ciò che ti fa entrare quando il codice non ti raggiunge, quindi conservali in un posto che',
    not: 'non sia',
    yourInbox: 'la tua casella di posta.',
    unusedIssued: '{count} non ancora usati, generati il {date}.',
    unusedOnly: '{count} non ancora usati.',
    writeThemDown: 'Annotali prima di lasciare questa pagina. Non sono conservati in una forma che possiamo leggere, quindi non potranno essere mostrati di nuovo.',
    passwordWhy: 'La chiediamo perché una nuova serie significa dieci nuovi modi per entrare in questo account.',
  },


  prefs: {
    title: 'Notifiche',
    subtitle: 'Di cosa ti avvisiamo e cosa lasciamo stare.',
    everything: 'Avvisami di quello che succede su ChoreConnect',
    everythingHint: 'Disattivalo e non riceverai nessun avviso sul telefono né nella campanella.',
    messages: 'Quando qualcuno mi scrive',
    messagesHint: 'I messaggi non letti restano comunque indicati sul fumetto: questo riguarda solo l’essere interrotto.',
    offWarning: 'Non riceverai avvisi sul telefono. Nella campanella continueremo comunque a segnalare alcune cose che è meglio che tu sappia: un lavoretto che ti era stato assegnato e viene annullato, o qualcosa di tuo che viene rimosso.',
  },

  alerts: {
    title: 'Avvisi sui lavoretti',
    onHere: 'Attivi qui',
    offHere: 'Disattivi qui',
    on: 'Le notifiche sono attive su questo dispositivo.',
    off: 'Le notifiche sono disattivate su questo dispositivo.',
    testSent: 'Inviata. Dovrebbe arrivare tra un secondo o due.',
    failed: 'Non ha funzionato.',
    refused: 'Il browser ha rifiutato. Puoi consentire di nuovo le notifiche dall’icona del lucchetto nella barra degli indirizzi.',
    blocked: 'Le notifiche sono bloccate per questo sito. Consentile dall’icona del lucchetto nella barra degli indirizzi, poi riprova.',
    iosNeedsInstall: 'Su iPhone o iPad devi prima aggiungere ChoreConnect alla schermata Home: vedi i passaggi qui sotto.',
    serverOff: 'Il server non è ancora configurato per inviare notifiche.',
    unsupported: 'Non ancora disponibile in questo browser. Su iPhone o iPad, aggiungi prima ChoreConnect alla schermata Home. Vedi i passaggi qui sotto.',
    unavailableHere: 'Le notifiche non sono disponibili in questo browser.',
    pitch: 'Ricevi una notifica sul telefono nel momento in cui viene pubblicato un lavoretto che ti interessa: sulla schermata di blocco, con un suono, senza aprire il sito. Non ti manderemo email sui lavoretti.',
    addToHomeScreen: 'Aggiungi a Home',
    iosStepsBefore: 'Tocca il pulsante di condivisione in Safari, scegli',
    iosStepsAfter: ', poi apri ChoreConnect da lì. Apple consente le notifiche solo ai siti aggiunti in questo modo: non è qualcosa che possiamo attivare noi per te.',
    commonestReason: 'Il motivo di gran lunga più comune è che ChoreConnect non è ancora stato aggiunto alla schermata Home.',
    appleNoSafari: '- Apple non consente le notifiche a un sito aperto in Safari, e non esiste alcuna impostazione che lo cambi.',
    deviceList: '- iPhone e iPad, Android, oppure computer e Chromebook.',
    blockedBody: 'Da qui non possiamo chiedertelo di nuovo: tocca il lucchetto (o l’icona accanto all’indirizzo) nel browser, consenti le notifiche per ChoreConnect, poi ricarica la pagina.',
    turnOffHere: 'Disattiva su questo dispositivo',
    sendTest: 'Mandami una prova',
    justAMoment: 'Un attimo...',
    turnOn: 'Attiva le notifiche',
    perDevice: 'Vale per ogni dispositivo. Attivarle qui non le attiva sul tuo portatile, e disattivarle qui non tocca gli altri dispositivi.',
    onlyTheseKinds: 'Solo questi tipi di lavoretto',
    nothingChosen: 'Nessuna scelta qui, quindi usiamo i tipi di lavoretto indicati sul tuo profilo. Selezionane alcuni per restringere ulteriormente.',
    chosenCount: '{count} selezionati.',
    onlyIfPays: 'Solo se paga almeno',
    anyAmount: 'Qualsiasi importo',
    onlyWithin: 'Solo entro',
    anyDistance: 'Qualsiasi distanza',
    quietFrom: 'Non disturbare dalle',
    quietUntil: 'alle',
    saveWhatIWant: 'Salva le mie preferenze',
    saved: 'Salvato.',
  },

  install: {
    chooseToSeeHow: 'Tocca il tuo dispositivo qui sopra e ti mostriamo come fare: sono due tocchi e circa un minuto.',
    whichDevice: 'Che dispositivo stai configurando?',

    deviceIos: 'iPhone o iPad',
    deviceAndroid: 'Telefono Android',
    deviceDesktop: 'Computer o Chromebook',
    addToHome: 'Aggiungi ChoreConnect alla schermata Home',
    desktopTitle: 'ChoreConnect su computer o Chromebook',

    iosWhy: 'Su iPhone o iPad questo è L’UNICO modo per ricevere le notifiche. Apple non le consente a un sito aperto nel browser, quindi questo passaggio viene per primo. Non è qualcosa che possiamo attivare noi al posto tuo.',
    iosNote: 'Deve essere Safari. Aggiungerlo da Chrome su iPhone o iPad non farà funzionare le notifiche.',
    ios1: 'Apri ChoreConnect in Safari.',
    ios2: 'Tocca il pulsante Condividi: su iPhone è in fondo allo schermo, su iPad in alto a destra. È il quadrato con la freccia che esce verso l’alto.',
    ios3: 'Scorri l’elenco verso il basso e tocca “Aggiungi a Home”.',
    ios4: 'Tocca “Aggiungi” in alto a destra.',
    ios5: 'Chiudi Safari e apri ChoreConnect dalla schermata Home. D’ora in poi va aperto da lì.',
    ios6: 'Vai nelle Impostazioni dentro l’app e attiva le notifiche. iOS ti chiederà di consentirle: tocca “Consenti”.',

    androidWhy: 'Su Android le notifiche funzionano in entrambi i casi, quindi questo è una comodità più che un obbligo: si apre a schermo intero, come un’app, senza barra degli indirizzi.',
    androidNote: 'Se non ti viene chiesto di consentire le notifiche, controlla in Impostazioni di Android, poi App, poi ChoreConnect o Chrome, poi Notifiche.',
    android1: 'Apri ChoreConnect in Chrome.',
    android2: 'Tocca i tre puntini in alto a destra.',
    android3: 'Tocca “Aggiungi a schermata Home” (su alcuni telefoni si chiama “Installa app”).',
    android4: 'Tocca “Installa” per confermare.',
    android5: 'Vai nelle Impostazioni dentro ChoreConnect e attiva le notifiche. Chrome te lo chiederà: tocca “Consenti”.',

    desktopWhy: 'Qui le notifiche funzionano già in una normale finestra del browser: installare è facoltativo e serve solo ad aprirlo in una finestra sua.',
    desktopNote: 'Safari su Mac non supporta affatto queste notifiche. Su computer usa Chrome, Edge o Firefox.',
    desktop1: 'Vai nelle Impostazioni dentro ChoreConnect e attiva le notifiche.',
    desktop2: 'Il browser te lo chiederà in cima allo schermo: clicca “Consenti”. Se te lo perdi, clicca sul lucchetto accanto all’indirizzo e consenti le notifiche da lì.',
    desktop3: 'Per installarlo anche come app (facoltativo): cerca l’icona di installazione all’estremità destra della barra degli indirizzi - uno schermo con una freccia, o un più - poi clicca “Installa”.',
    desktop4: 'Su Chromebook è uguale, e poi compare nel launcher insieme alle altre app.',
    getOnYourPhone: 'Metti ChoreConnect sul telefono',
    twoMinutesOnce: 'Due minuti, una volta sola. Si apre come un’app, ed è il modo per sapere le cose senza dover controllare in continuazione.',
    nothingToDo: 'Qui non c’è nulla da fare: stai già aprendo ChoreConnect nel modo giusto.',
    onForThisDevice: 'Attive su questo dispositivo. Puoi disattivarle di nuovo da qui quando vuoi.',
    buttonAppearsWhen: 'Il pulsante compare quando ChoreConnect è sulla schermata Home e lo hai aperto da lì. È una regola di Apple, non nostra: non abbiamo modo di attivarlo noi per te.',
    blockedBodyShort: 'Da qui non possiamo chiedertelo di nuovo: tocca il lucchetto accanto all’indirizzo, consenti le notifiche, poi ricarica.',
    phoneWillAsk: 'Il telefono ti chiederà una conferma. Niente di notte, e puoi disattivarle quando vuoi.',
    perDeviceShort: 'Vale per ogni dispositivo. Farlo sul telefono non lo fa sul portatile.',
    notificationsOn: 'Fatto. Le notifiche sono attive su questo dispositivo.',
  },


  suspend: {
    needReason: 'Scrivi il motivo. Viene mostrato a questa persona quando prova ad accedere.',
    badLength: 'Scegli una durata tra 1 e 365 giorni, oppure a tempo indeterminato.',
    suspending: 'Sospensione in corso...',
    indefinitely: 'Sospendi a tempo indeterminato',
    forDays: 'Sospendi per {days} giorni',
    howLong: 'Per quanto tempo',
    numberOfDays: 'Numero di giorni',
    whyTheyRead: 'Motivo - lo leggeranno',
    titleFor: 'Vuoi sospendere {name}?',
    thisPerson: 'questa persona',
    signedOutEverywhere: 'Verrà disconnesso da tutti i dispositivi immediatamente e non potrà accedere finché la sospensione non finisce.',
    custom: 'Personalizzata',
    reasonPlaceholder: 'Scritto a loro, non su di loro. Di’ cosa è successo e cosa deve cambiare.',
    reasonHint: 'Viene mostrato parola per parola a ogni tentativo di accesso. Fino a 500 caratteri.',
    indefiniteWarning: 'Una sospensione a tempo indeterminato non finisce da sola. Alla persona viene detto che non è temporanea.',
  },

  takedown: {
    needReason: 'Scrivi il motivo. Viene inviato a questa persona, parola per parola.',
    titleOffer: 'Vuoi rimuovere la disponibilit\u00e0 di {name}?',
    titleChore: 'Vuoi rimuovere questo lavoretto?',
    takingDown: 'Rimozione in corso...',
    takeItDown: 'Rimuovilo',
    effectOffer: 'Sparisce subito dall\u2019elenco degli studenti disponibili. Nessuno potr\u00e0 chiedere di prenotarlo finch\u00e9 non ripubblica.',
    effectChore: 'Sparisce subito dalla bacheca. Nessun altro potr\u00e0 candidarsi.',
    whyTheyRead: 'Motivo - lo leggeranno',
    hintOffer: 'Viene scritto a loro. Spiega cosa non andava e cosa andrebbe bene.',
    hintChore: 'Viene scritto a loro. Spiega cosa non andava nel testo e cosa andrebbe bene.',
    reasonHint: 'Viene inviato come notifica, parola per parola. Fino a 500 caratteri.',
    nothingDeletedBefore: 'Non viene eliminato nulla. Se si rivelasse un errore, si può ripristinare dalla scheda',
    takenDownTab: 'Rimossi',
    nothingDeletedAfter: ', e anche di questo la persona viene informata.',
  },

  subscribe: {
    freeTitle: 'ChoreConnect \u00e8 gratis',
    canDoEverything: 'Il tuo account pu\u00f2 gi\u00e0 fare tutto',
    thanks: 'Grazie! Il tuo abbonamento \u00e8 ora attivo.',
    cancelled: 'Pagamento annullato: non \u00e8 stato addebitato nulla.',
    devOn: 'Abbonamento attivato in modalit\u00e0 sviluppo. Non \u00e8 stato preso alcun pagamento.',
    perk1: 'Pubblica tutti i lavoretti che vuoi, tutto l\u2019anno',
    perk2: 'Vedi tutti gli studenti che si candidano, con le loro recensioni',
    perk3: 'Accetta uno studente e indica una riserva',
    perk4: 'Valutazioni e recensioni scritte in entrambe le direzioni',
    perk5: 'Notifiche via email e nell\u2019app',
    nothingToPay: 'Non c’è nulla da pagare e nulla da configurare.',
    postAChore: 'Pubblica un lavoretto',
    keepingFree: 'ChoreConnect è gratuito, e non c’è alcun piano per cambiarlo. Se un giorno dovesse cambiare, lo diremmo a tutti con largo anticipo: nessuno lo scoprirebbe da una fattura.',
    membershipTitle: 'Iscrizione a ChoreConnect',
    membershipSubtitle: '5 € all’anno per pubblicare lavoretti. Gli studenti non pagano mai nulla.',
    runsUntil: 'Valida fino al',
    manageOrCancel: 'Gestisci o disdici l’iscrizione',
    perYear: 'all’anno',
    stripeHandles: 'Il pagamento è gestito da Stripe. I dati della tua carta non passano mai da questo sito.',
    studentBefore: 'Sei uno studente? Non ti serve mai:',
    goFindWork: 'vai a cercare lavoretti',
  },

  support: {
    title: 'Mandare avanti ChoreConnect',
    subtitle: 'Quanto costa, e come puoi dare una mano se ti va.',
    thanks: 'Grazie.',
    whereMoneyGoes: 'Dove vanno i soldi',
    chipIn: 'Dai una mano',
    noPaymentTaken: 'Non è stato addebitato nulla. Nessun problema: il sito funziona esattamente come prima.',
    notACompany: 'ChoreConnect non è un’azienda e nessuno ci guadagna uno stipendio. È gratuito per chi pubblica lavoretti e per gli studenti, e i soldi di ogni lavoretto passano direttamente fra le due persone: il sito non li tocca mai.',
    whatItCosts: 'Quello che costa è tenerlo online: il dominio, il server e le email che recapitano ai genitori le richieste di consenso. Le donazioni servono a questo e a nient’altro.',
    oneOffNoBadge: 'Una tantum, mai un abbonamento: non c’è nulla da disdire in seguito. Il pagamento è gestito da Stripe e i dati della tua carta non passano da questo sito. Donare non compra nulla: nessun distintivo, nessuna priorità e niente che qualcun altro possa vedere.',
    notSwitchedOn: 'Al momento le donazioni non sono attive. Il sito funziona benissimo anche senza.',
    thanksBody: 'I tuoi {amount} vanno a coprire le spese di quest’anno. Stripe ti manderà una ricevuta via email.',
    notConfirmedYet: 'Stripe non ha ancora confermato il pagamento. Se va a buon fine, Stripe ti manderà una ricevuta via email.',
    otherAmount: 'Un altro importo (€)',
    otherAmountHint: 'Qualsiasi cifra da {min} a {max}.',
    otherAmountInvalid: 'Scrivi un importo da {min} a {max}.',
    give: 'Dona',
    devNoStripe: 'Su questo computer i pagamenti con carta non sono configurati, quindi questi pulsanti non funzionano. Aggiungi STRIPE_SECRET_KEY a backend/.env.',
  },


  moderation: {
    tabIssues: 'Problemi',
    issuesEmptyTitle: 'Nessuno ha segnalato un problema',
    issuesEmptyBody: 'Quando qualcuno ci dice che qualcosa non funziona, la sua conversazione compare qui.',
    issuesBackToList: 'Torna all’elenco',
    issuesNothingYet: 'Ancora niente in questa conversazione.',
    issuesReply: 'La tua risposta',
    issuesReplyPlaceholder: 'Rispondi...',
    issuesSend: 'Rispondi',
    issuesYou: 'Tu:',
    issuesMinor: '{age} anni, minorenne',
    issuesClosedAccount: 'account chiuso',
    issuesAnonymousNote:
      'Per la persona la risposta arriva da “Moderatore”. Il tuo nome, la tua foto e il tuo profilo non vengono mai mostrati qui.',
    emailWhat: 'Che cosa fa',
    emailOnTitle: 'Le email vengono inviate normalmente',
    emailOnBody:
      'Disattivale quando le 300 del giorno sono finite. Mentre sono disattivate, iscriversi, confermare un indirizzo, reimpostare la password e chiedere il consenso a un genitore vengono rifiutati con una spiegazione, invece di fallire in silenzio.',
    emailOffTitle: 'Le email sono DISATTIVATE',
    emailOffBody:
      'Nessuno può iscriversi, reimpostare la password, confermare un indirizzo o far arrivare una richiesta di consenso a un genitore. Tutti vedono un avviso che lo spiega. Riattivale appena la quota si azzera.',
    emailTurnOff: 'Disattiva le email',
    emailTurnOn: 'Riattiva le email',
    emailNowOff: 'Email disattivate. Lo stiamo dicendo a tutti e non verrà inviato niente.',
    emailNowOn: 'Le email sono di nuovo attive.',
    title: 'Moderazione',
    loading: 'Carico le segnalazioni...',
    notFound: 'Pagina non trovata',
    nothingHere: 'Qui non c\u2019\u00e8 niente.',
    nothingWithStatus: 'Niente qui',
    noReportsWithStatus: 'Nessuna segnalazione con questo stato.',
    whatDidYouDecide: 'Cosa hai deciso?',
    tabOpen: 'Aperte',
    tabInvestigating: 'In esame',
    tabResolved: 'Risolte',
    tabDismissed: 'Archiviate',
    tabAudit: 'Registro attivit\u00e0',

    reasonInappropriate: 'Comportamento inappropriato',
    reasonUnsafe: 'L\u2019ha fatto sentire in pericolo',
    reasonUnsafeWork: 'Gli \u00e8 stato chiesto qualcosa di pericoloso',
    reasonNoShow: 'Non si \u00e8 presentato',
    reasonFake: 'Il profilo potrebbe essere falso',
    reasonAbusiveReview: 'Recensione offensiva',
    reasonOther: 'Altro',

    openingConversation: 'Apro la conversazione...',
    offerConversation: 'Una domanda sulla disponibilità di uno studente',
    questionNotBooked: 'Una domanda fatta prima di candidarsi. Questo studente non era stato assegnato al lavoretto.',
    questionBadge: 'Domanda prima della candidatura',
    offerGone: 'la disponibilità è stata rimossa',
    offerStillUp: 'la disponibilità è ancora pubblicata',
    noConversation: 'Qui non c\u2019\u00e8 nessuna conversazione.',
    readingPrivate: 'Stai leggendo una conversazione privata tra un adulto e uno studente.',

    loadingTakedowns: 'Carico le rimozioni...',
    nothingTakenDown: 'Non \u00e8 stato rimosso niente',
    reasonGiven: 'Motivo indicato:',
    offMarketplace: 'Tolto dalla bacheca',
    offWhosFree: 'Non in elenco',
    availability: 'Disponibilit\u00e0',

    readingLog: 'Leggo il registro...',
    nothingRecorded: 'Niente registrato',
    noEntriesMatch: 'Nessuna voce corrisponde.',
    theSiteItself: 'Il sito stesso',
    logEverything: 'Tutto',
    logConversationsRead: 'Conversazioni lette',
    logSuspensions: 'Sospensioni',
    logReports: 'Segnalazioni',
    logCancellations: 'Annullamenti',
    logConsentGiven: 'Consensi dati',
    backToList: 'Torna all’elenco',
    poster: 'Committente',
    student: 'Studente',
    fromStudent: 'studente',
    fromPoster: 'committente',
    contactDetails: 'contatti',
    hidden: 'nascosto',
    putItBack: 'Ripristina',
    hideFromBoth: 'Nascondi a entrambi',
    hidingNote: 'Nascondere toglie il messaggio dallo schermo delle due persone. Il testo resta agli atti: è una prova, e chi potesse cancellare quello che ha scritto potrebbe cancellare anche la prova.',
    onlyThreadsBefore: 'Solo le conversazioni che sono state',
    reported: 'segnalate',
    onlyThreadsMiddle: ', o in cui qualcuno ha',
    sharedContact: 'condiviso un numero di telefono o un’email',
    onlyThreadsAfter: '. Tutto il resto è privato, e il server lo rifiuta anche con un link diretto.',
    nothingToLookAt: 'Niente da esaminare',
    nothingToLookAtBody: 'Nessuna conversazione è stata segnalata e nessuno si è scambiato contatti. È il risultato che si vuole.',
    urgentReport: 'segnalazione urgente',
    openReports: { one: '{count} segnalazione aperta', other: '{count} segnalazioni aperte' },
    choreLabel: 'Lavoretto',
    auditNote: 'Dal più recente. Niente qui viene mai modificato o rimosso: è proprio questo il punto.',
    recorded: 'Registrato.',
    postedBy: 'Pubblicato da',
    posterTag: '(committente) e',
    studentTag: '(studente',
    handled: '{count} gestite',
    withContactDetails: '{count} con contatti',
    urgent: 'Urgente',
    outcomeLabel: 'Esito:',
    takeDownChore: 'Rimuovi il lavoretto',
    takeDownOffer: 'Rimuovi la disponibilità',
    suspend: 'Sospendi',
    reinstate: 'Riattiva',
    recordDecision: 'Registra la decisione',
    lookingIntoIt: 'Sto verificando',
    dismiss: 'Archivia',
    resolved: 'Risolta',
    outcomePlaceholder: 'Parlato con entrambi, nessun altro provvedimento. / Sospeso in attesa di contatto con la scuola.',
    outcomeHint: 'Scrivilo anche quando decidi di non fare nulla. È la prova che hai guardato.',
    takedownsEmptyBody: 'Qui compaiono i lavoretti e le disponibilità rimossi da un moderatore, con la motivazione, così da poterli ripristinare.',
    tabReports: 'Segnalazioni',
    tabTakenDown: 'Rimossi',
    readConversation: 'Leggi la conversazione',
    aboutLabel: 'Riguarda:',
    aChore: 'un lavoretto',
    fromWhom: 'Da {name}',
    agedYears: ' ({age} anni)',
    statusLabel: 'Stato: {status}',
    urgentWaiting: { one: '{count} segnalazione urgente in attesa.', other: '{count} segnalazioni urgenti in attesa.' },
    openWaiting: { one: '{count} segnalazione aperta.', other: '{count} segnalazioni aperte.' },
    call112: 'Se qualcuno potrebbe essere in pericolo immediato, chiama il 112 prima di fare qualsiasi cosa qui.',
    flaggedStrip: { one: '{count} conversazione segnalata dal rilevatore di contatti', other: '{count} conversazioni segnalate dal rilevatore di contatti' },
    flaggedStripBody: 'Nessuno le ha segnalate. Compaiono perché è stato condiviso un numero di telefono o un’email: è lo schema che nessuno segnala.',
  },


  tabs: {
    open: 'Aperti',
    expired: 'Scaduti',
    bookedIn: 'Assegnati',
    completed: 'Completati',
    gotTheJob: 'Ottenuti',
    backup: 'Riserva',
    waitingToHear: 'In attesa',
    noneOfThese: 'Nessun lavoretto in \u201c{kind}\u201d',
  },

  donate: {
    title: 'ChoreConnect va avanti con le donazioni',
    notJustNow: 'Non adesso',
    niceOne: 'Bel lavoro: un altro lavoretto è andato a buon fine. ChoreConnect è gratuito e non c’è nessuna azienda dietro, ma tenerlo online costa: il server, il dominio e le email che recapitano ai genitori le richieste di consenso.',
    takenToStripe: 'Per il pagamento si passa sulla pagina di Stripe.',
    otherAmountLink: 'Un altro importo',
    runsOnDonations: 'Va avanti con le donazioni di chi lo usa. Se puoi contribuire alle spese di quest’anno, è di grande aiuto.',
    oneOff: 'Una tantum, mai un abbonamento. Il pagamento è gestito da Stripe: i dati della tua carta non passano da questo sito. Donare non cambia nulla nel modo in cui ChoreConnect funziona per te o per gli studenti: non dà priorità, né distintivi, né alcunché di visibile agli altri.',
  },


  time: {
    dateAtTime: '{date} alle {time}',
    flexible: 'orario flessibile',
    dateFlexible: '{date}, a un orario che va bene a entrambi',
    justNow: 'proprio ora',
    minutesAgo: { one: '{count} minuto fa', other: '{count} minuti fa' },
    hoursAgo: { one: '{count} ora fa', other: '{count} ore fa' },
    daysAgo: { one: '{count} giorno fa', other: '{count} giorni fa' },
    monthsAgo: { one: '{count} mese fa', other: '{count} mesi fa' },
    yearsAgo: { one: '{count} anno fa', other: '{count} anni fa' },
    inThePast: 'già passato',
    inMinutes: { one: 'tra {count} minuto', other: 'tra {count} minuti' },
    inHours: { one: 'tra {count} ora', other: 'tra {count} ore' },
    inDays: { one: 'tra {count} giorno', other: 'tra {count} giorni' },
  },

  misc: {
    reportedByMinor: 'Segnalato da un minore',
    alreadySuspended: 'Gi\u00e0 sospeso',
    whatYouDid: 'Cosa hai fatto, e perch\u00e9',
    whyYouCanSee: 'Perch\u00e9 puoi vedere questa conversazione',
    nothingSent: 'Non \u00e8 mai stato inviato niente.',
    membershipActive: 'Il tuo abbonamento \u00e8 attivo',
    cardsOff: 'I pagamenti con carta non sono ancora attivi.',
    alreadyInstalled: 'Gi\u00e0 sulla schermata Home.',
    alreadyInstalledBody: 'Non devi fare nulla: stai gi\u00e0 aprendo ChoreConnect nel modo giusto.',
    allowNotifications: 'Consenti le notifiche',
    blockedHere: 'Le notifiche sono bloccate per questo sito.',
    browserCannot: 'Questo browser non pu\u00f2 mostrare notifiche.',
    iosFirstStep: 'Su iPhone o iPad serve prima un passaggio.',
    fullSteps: 'Tutti i passaggi per il tuo dispositivo',
    stepByStep: 'Istruzioni passo passo per il tuo dispositivo',
    tellMeNewJob: 'Avvisami quando esce un nuovo lavoretto',
    mustBeInstalled: 'Su iPhone o iPad \u00e8 obbligatorio',
    twoMinutes: 'Ci vogliono circa due minuti.',
    plainWay: 'Non funziona ancora? Prova nel modo semplice.',
    someone: 'Qualcuno',
    deletedUser: 'Utente eliminato',
    metresAway: '{count} m da casa',
    kmAway: '{count} km da casa',
    kmByRoad: '{count} km da casa su strada',
    mByRoad: '{count} m da casa su strada',
    kmOnFoot: '{count} km a piedi',
    mOnFoot: '{count} m a piedi',
    kmStraight: '{count} km da casa in linea d’aria',
    mStraight: '{count} m da casa in linea d’aria',
    accountClosed: 'Questo account è stato chiuso',
    starRating: 'Valutazione in stelle',
    viewDetails: 'Vedi i dettagli',
    removeNotification: 'Elimina la notifica',
  },

  footer: {
    reportIssue: 'Segnala un problema',
    tagline: 'Mettiamo in contatto chi ha bisogno di lavoretti da far fare e gli studenti che vorrebbero guadagnare. Paga giusta, recensioni vere e un po’ di indipendenza.',
    forStudents: 'Per gli studenti',
    forPosters: 'Per pubblicare lavoretti',
    browse: 'Cerca lavoretti',
    createAccount: 'Crea un account',
    membership: 'Abbonamento',
    postChore: 'Pubblica un lavoretto',
    privacy: 'Privacy',
    terms: 'Termini',
    builtFree: 'Fatto per gli studenti. Gratis per tutti.',
    builtPaid: 'Fatto per gli studenti. 5 € all’anno per chi pubblica, sempre gratis per chi lavora.',
  },
};
